import React, { useEffect, useMemo, useState, useRef, useLayoutEffect } from 'react';
import MoreOptions from './MoreOptions';
import QuotationUpdateRequestHistory from './QuotationUpdateRequestHistory';
import LabourRFQTable from './LabourRFQTable';
import ContractorRFQTable from './ContractorRFQTable';
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from 'react-redux';
import ReactHtmlParser from "react-html-parser";
import { handleDownloadComparisonExcel } from './exportExcelHelper';
import LoadingModal from 'views/Rfqs/LoadingModal';
import { useSnackbar } from 'notistack';
import ComparisonExtraCharges from './ComparisonExtraCharges';
import SelectionMaterialRFQTable from './SelectionMaterialRFQTable';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import MaterialRFQTable from './MaterialRFQTable';
import { updateRFQComparisonExclusion, requestRFQQuotationUpdate } from 'actions/rfqActions';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { createPortal } from 'react-dom';

const getComparisonLineItemKey = (type, item, index) =>
    `${type}:${String(item?._id ?? item?.id ?? index)}`;

const getComparisonLineItemLabel = (value, fallback) => {
    const text = String(value || "")
        .replace(/<[^>]*>/g, " ")
        .replace(/&nbsp;/gi, " ")
        .replace(/\s+/g, " ")
        .trim();

    return text || fallback;
};

const ComparisontableTesting = ({ rfqData, data, userProfile, suppliersWithQuotation, closeRfqForm = false,
    selectedMaterialsMap, setSelectedMaterialsMap, chargeInputs, setChargeInputs,
    discountOnBasicAmountInputs = {}, setDiscountOnBasicAmountInputs,
    totalSelectedQuotationValue, setTotalSelectedQuotationValue, selectedRatesTab = false, isEditRatesLocked = false, setSelectedVendors, selectedVendors, openPdfForSupplier }) => {

    const [openNoteIndex, setOpenNoteIndex] = useState(null);
    const [openDropdown, setOpenDropdown] = useState({ rowIndex: null, vendorIndex: null });
    const [withGst, setWithGst] = useState(false); // GST state
    const [sortedSuppliers, setSortedSuppliers] = useState([]);
    const { enqueueSnackbar } = useSnackbar();
    const dispatch = useDispatch();
    const isSupplierExcludedFromComparison = (supplier = {}) =>
        supplier?.exclusionDetails?.isExcludedFromComparison === true;
    const hasVendorSource = sortedSuppliers?.some(
        supplier => Boolean(supplier?.vendorSource)
    );
    const [excelLoading, setExcelLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState("");
    const [excelError, setExcelError] = useState(null);
    const [totalLowestPriceSum, setTotalLowestPriceSum] = useState(0);
    const [totalLowestLabourPriceSum, setTotalLowestLabourPriceSum] = useState(0);
    const [otherstotalLowestPriceSum, setOtherstotalLowestPriceSum] = useState(0);
    const [selectedCheckboxes, setSelectedCheckboxes] = useState({});
    const [totalLowestContractorPriceSum, setTotalLowestContractorPriceSum] = useState(0);
    const [collapsedVendorIds, setCollapsedVendorIds] = useState(() => new Set());
    const [hiddenLineItemKeys, setHiddenLineItemKeys] = useState(() => new Set());
    const [openSummaryRevisionTrails, setOpenSummaryRevisionTrails] = useState(() => new Set());
    const comparisonTableRef = useRef(null);
    const hiddenRowsTriggerRef = useRef(null);
    const hiddenVendorsTriggerRef = useRef(null);
    const visibilityMenuRef = useRef(null);
    const [openVisibilityMenu, setOpenVisibilityMenu] = useState(null);
    const [visibilityMenuPosition, setVisibilityMenuPosition] = useState(null);
    const [comparisonHeaderHeight, setComparisonHeaderHeight] = useState(null);
    const hasVisibleCharges = Object.values(rfqData.otherCharges || {}).some(charge => charge);
    const isAdvancedTransport = rfqData?.transportOption === "perUnitAmount";
    const ALLOW_MULTI_VENDOR_PER_MATERIAL = true;
    const [tab, setTab] = useState("SomeDefaultTab"); // or however you're managing it
    const canSelectRates = closeRfqForm || selectedRatesTab;
    const isClosedMaterialRfq = rfqData?.rfqType === "material" && rfqData?.status === "CLOSED";
    const parseNumber = (value, fallback = 0) => {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : fallback;
    };

    const formatIstDate = (dateValue) => {
        if (!dateValue) return "N/A";
        const parsedDate = new Date(dateValue);
        if (Number.isNaN(parsedDate.getTime())) return "N/A";

        return parsedDate.toLocaleDateString("en-IN", {
            timeZone: "Asia/Kolkata",
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    };

    const formatSummaryRevisionAmount = (value) =>
        new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(parseNumber(value));

    const getQuotationSetSummaryAmount = (quotationSet, summaryType) => {
        if (summaryType === "material-amount") {
            return parseNumber(quotationSet?.totalWithoutGst);
        }

        const explicitGrandTotalValue = quotationSet?.grandTotalWithoutGst;
        const explicitGrandTotal = Number(explicitGrandTotalValue);
        if (explicitGrandTotalValue !== null && explicitGrandTotalValue !== undefined && Number.isFinite(explicitGrandTotal)) {
            return explicitGrandTotal;
        }

        return parseNumber(quotationSet?.totalWithoutGst) + parseNumber(quotationSet?.othersTotalWithoutGst);
    };

    const getQuotationUpdateRemark = (quotationSet) => {
        const reason = quotationSet?.quotationUpdateReason;
        if (typeof reason === "string") return reason.trim();
        return reason?.remark || reason?.reason || "";
    };

    const getSummaryRevisionTrail = (supplier, summaryType) => {
        let previousQuotationAmount = null;

        return (supplier?.vendorQuotations?.quotationSets || [])
            .reduce((trail, quotationSet, quotationIndex) => {
                const amount = getQuotationSetSummaryAmount(quotationSet, summaryType);
                const hasChanged = previousQuotationAmount === null || Math.abs(amount - previousQuotationAmount) > 0.01;
                previousQuotationAmount = amount;

                if (!hasChanged) return trail;

                trail.push({
                    label: `R${quotationIndex}`,
                    date: formatIstDate(quotationSet?.submittedAt || quotationSet?.updatedAt || quotationSet?.createdAt),
                    submittedBy: quotationSet?.submittedBy || quotationSet?.updatedBy || "-",
                    amount,
                    remark: getQuotationUpdateRemark(quotationSet),
                });
                return trail;
            }, [])
            .map((revision, revisionIndex, trail) => {
                const previousRevision = trail[revisionIndex - 1];
                const difference = previousRevision ? revision.amount - previousRevision.amount : null;
                const differencePercent = previousRevision && previousRevision.amount !== 0
                    ? (Math.abs(difference) / Math.abs(previousRevision.amount)) * 100
                    : null;

                return {
                    ...revision,
                    label: `${revision.label}${revisionIndex === trail.length - 1 ? " (current)" : ""}`,
                    difference,
                    differencePercent,
                };
            });
    };

    const toggleSummaryRevisionTrail = (event, summaryType, supplierId, trailLength) => {
        event.stopPropagation();
        if (trailLength <= 1) return;

        const key = `${summaryType}-${supplierId}`;
        setOpenSummaryRevisionTrails((previous) => {
            const next = new Set(previous);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const formatSummaryRevisionDifference = (difference, differencePercent) => {
        if (difference === null || difference === undefined) return "-";
        const numericDifference = Number(difference);
        if (!Number.isFinite(numericDifference)) return "-";

        return (
            <>
                {formatSummaryRevisionAmount(Math.abs(numericDifference))}{" "}
                {numericDifference < 0 && "\u25BC"}
                {numericDifference > 0 && "\u25B2"}
                {differencePercent !== null && differencePercent !== undefined && (
                    <span className="ml-1 text-[10px]">({Number(differencePercent).toFixed(2)}%)</span>
                )}
            </>
        );
    };

    const renderSummaryRevisionTrail = (supplier, summaryType) => {
        if (rfqData?.rfqType !== "material") return null;

        const trail = getSummaryRevisionTrail(supplier, summaryType);
        if (trail.length <= 1) return null;

        const supplierId = supplier?.supplierId || supplier?._id;
        const trailKey = `${summaryType}-${supplierId}`;
        const isOpen = openSummaryRevisionTrails.has(trailKey);
        const revisionCount = trail.length - 1;

        return (
            <>
                <button
                    type="button"
                    onClick={(event) => toggleSummaryRevisionTrail(event, summaryType, supplierId, trail.length)}
                    className="inline-flex shrink-0 items-center justify-center whitespace-nowrap rounded-full border border-blue-200 bg-blue-50 px-2 py-[1px] text-[10px] font-semibold leading-4 text-blue-700 hover:border-blue-300 hover:bg-blue-100"
                >
                    {isOpen
                        ? "Hide revisions"
                        : `${revisionCount} ${revisionCount === 1 ? "revision" : "revisions"}`}
                </button>

                {isOpen && (
                    <div
                        className="order-last mt-2 w-full min-w-[520px] basis-full rounded-xl border border-blue-100 bg-[#F8FAFC] px-3 py-3 text-left text-[11px] text-[#3F4B64] shadow-lg"
                        onClick={(event) => event.stopPropagation()}
                    >
                        <div className="grid grid-cols-[52px_120px_minmax(90px,1fr)_minmax(100px,1fr)] gap-x-2 border-b border-gray-400 pb-2 font-semibold text-gray-500">
                            <span>Round</span>
                            <span className="text-center">Updated by</span>
                            <span className="text-right">Amount</span>
                            <span className="text-right">Difference</span>
                        </div>
                        {trail.map((revision, revisionIndex) => (
                            <div key={`${trailKey}-${revision.label}`} className="border-b border-gray-300 py-2">
                                <div className="grid grid-cols-[52px_120px_minmax(90px,1fr)_minmax(100px,1fr)] gap-x-2">
                                    <span className={`font-semibold ${revisionIndex === trail.length - 1 ? "text-blue-700" : "text-gray-600"}`}>
                                        {revision.label}
                                    </span>
                                    <span className="whitespace-normal break-words text-center leading-4">
                                        <span className="block">{revision.submittedBy}</span>
                                        <span className="block">{revision.date}</span>
                                    </span>
                                    <span className={`text-right font-semibold ${revisionIndex === trail.length - 1 ? "text-blue-700" : "text-[#2D2D11]"}`}>
                                        Rs. {formatSummaryRevisionAmount(revision.amount)}
                                    </span>
                                    <span className={`text-right font-semibold ${revision.difference < 0 ? "text-emerald-600" : revision.difference > 0 ? "text-red-600" : "text-[#777777]"}`}>
                                        {formatSummaryRevisionDifference(revision.difference, revision.differencePercent)}
                                    </span>
                                </div>
                                {revision.remark && (
                                    <p className="mt-1 rounded-md bg-white px-2 py-1 text-left text-[10px] leading-4 text-[#5F6F8A]">
                                        <span className="font-semibold text-[#2D2D11]">Remarks:</span>{" "}
                                        {revision.remark}
                                    </p>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </>
        );
    };

    const hasMaterialBudgetColumns = (rfqData?.materials || []).some(
        item => parseNumber(item?.budget) > 0 || parseNumber(item?.budgetAmount) > 0
    );
    const hasLabourBudgetColumns = (rfqData?.labourDetails || []).some(
        item => parseNumber(item?.budget) > 0 || parseNumber(item?.budgetAmount) > 0
    );
    const hasContractorBudgetAmountColumn = (rfqData?.providingFixingDetails || []).some(
        item => parseNumber(item?.budgetAmount) > 0
    );

    const comparisonLineItems = useMemo(() => {
        let primaryItems = [];

        if (rfqData?.rfqType === "material") {
            primaryItems = (rfqData?.materials || []).map((item, index) => ({
                key: getComparisonLineItemKey("material", item, index),
                label: getComparisonLineItemLabel(item?.name, `Material ${index + 1}`),
                serialNumber: index + 1,
            }));
        }

        if (rfqData?.rfqType === "labour") {
            primaryItems = (rfqData?.labourDetails || []).map((item, index) => ({
                key: getComparisonLineItemKey("labour", item, index),
                label: getComparisonLineItemLabel(item?.skill, `Labour item ${index + 1}`),
                serialNumber: index + 1,
            }));
        }

        if (rfqData?.rfqType === "contractor") {
            primaryItems = (rfqData?.providingFixingDetails || []).flatMap((section, sectionIndex) =>
                [
                    {
                        key: getComparisonLineItemKey("contractor-section", section, sectionIndex),
                        label: getComparisonLineItemLabel(section?.sectionName, `Section ${sectionIndex + 1}`),
                        serialNumber: sectionIndex + 1,
                    },
                    ...(section?.scopes || []).map((scope, scopeIndex) => ({
                        key: getComparisonLineItemKey("contractor", scope, `${sectionIndex}-${scopeIndex}`),
                        label: getComparisonLineItemLabel(scope?.scopeOfWork, `Scope ${sectionIndex + 1}.${scopeIndex + 1}`),
                        serialNumber: `${sectionIndex + 1}.${scopeIndex + 1}`,
                    })),
                ]
            );
        }

        const extraChargeItems = Object.keys(rfqData?.otherCharges || {})
            .filter(chargeType => rfqData?.otherCharges?.[chargeType])
            .map((chargeType) => ({
                key: getComparisonLineItemKey("extra-charge", { id: chargeType }, chargeType),
                label: chargeType
                    .replace(/([A-Z])/g, " $1")
                    .trim()
                    .replace(/\b\w/g, character => character.toUpperCase()),
                serialNumber: "Extra",
            }));

        return [...primaryItems, ...extraChargeItems];
    }, [rfqData?.labourDetails, rfqData?.materials, rfqData?.otherCharges, rfqData?.providingFixingDetails, rfqData?.rfqType]);

    const hiddenLineItems = comparisonLineItems.filter(item => hiddenLineItemKeys.has(item.key));

    useEffect(() => {
        setHiddenLineItemKeys(new Set());
        setOpenSummaryRevisionTrails(new Set());
    }, [rfqData?._id, rfqData?.rfqId]);
    const showBudgetRateColumn =
        (rfqData?.rfqType === "material" && hasMaterialBudgetColumns) ||
        (rfqData?.rfqType === "labour" && hasLabourBudgetColumns);
    const showBudgetAmountColumn =
        (rfqData?.rfqType === "material" && hasMaterialBudgetColumns) ||
        (rfqData?.rfqType === "labour" && hasLabourBudgetColumns) ||
        (rfqData?.rfqType === "contractor" && hasContractorBudgetAmountColumn);

    const getSelectedQuantityForMaterial = (materialId, excludeVendorId = null) =>
        Object.entries(selectedMaterialsMap || {}).reduce((sum, [supplierId, materials]) => {
            if (excludeVendorId && String(supplierId) === String(excludeVendorId)) return sum;
            const selectedMaterial = materials?.find(item => String(item.materialId) === String(materialId));
            return sum + parseNumber(selectedMaterial?.selectedQuantity);
        }, 0);

    const calculateSelectedTransportCost = (transportRate, selectedQuantity, existingTransportCost = 0) =>
        isAdvancedTransport
            ? parseNumber(transportRate) * parseNumber(selectedQuantity)
            : parseNumber(existingTransportCost);

    const calculateSelectedSupplierPrice = (itemRate, selectedQuantity, transportCost) =>
        (parseNumber(itemRate) * parseNumber(selectedQuantity)) + parseNumber(transportCost);

    const normalizeDiscountPercent = (value) => {
        if (value === "" || value === null || value === undefined) return 0;
        const numericValue = parseNumber(value);
        if (numericValue <= 0) return 0;
        if (numericValue > 100) return 100;
        return numericValue;
    };

    const getLatestQuotationSet = (supplier) =>
        supplier?.vendorQuotations?.quotationSets?.slice(-1)[0] || {};

    const getDiscountOnBasicAmountPercent = (supplierId, supplier) => {
        const editedValue = discountOnBasicAmountInputs?.[supplierId];
        if (editedValue !== undefined && editedValue !== null && editedValue !== "") {
            return normalizeDiscountPercent(editedValue);
        }

        return normalizeDiscountPercent(getLatestQuotationSet(supplier)?.discountOnBasicAmountPercent);
    };

    const getDiscountOnBasicAmount = (supplierId, selectedMaterialAmount, supplier) =>
        (parseNumber(selectedMaterialAmount) * getDiscountOnBasicAmountPercent(supplierId, supplier)) / 100;

    const getMaterialVendorDiscountedSelectedAmount = (supplierId, selectedMaterialAmount, supplier) =>
        Math.max(0, parseNumber(selectedMaterialAmount) - getDiscountOnBasicAmount(supplierId, selectedMaterialAmount, supplier));

    const hasStoredCommercialDiscount = sortedSuppliers?.some((supplier) => {
        const latestSet = getLatestQuotationSet(supplier);
        return normalizeDiscountPercent(latestSet?.discountOnBasicAmountPercent) > 0 ||
            parseNumber(latestSet?.discountOnBasicAmount) > 0;
    });

    const handleDiscountOnBasicAmountChange = (supplierId, value) => {
        const sanitizedValue = String(value || "").replace(/[^\d.]/g, "");
        const decimalParts = sanitizedValue.split(".");
        const normalizedValue = decimalParts.length > 1
            ? `${decimalParts[0]}.${decimalParts.slice(1).join("").slice(0, 2)}`
            : decimalParts[0];
        const clampedValue = normalizedValue === "" ? "" : String(Math.min(100, Math.max(0, Number(normalizedValue))));

        setDiscountOnBasicAmountInputs?.((prev) => ({
            ...prev,
            [supplierId]: clampedValue,
        }));
    };

    const calculateSelectedLabourPrice = (labourRate, selectedQuantity) =>
        parseNumber(labourRate) * parseNumber(selectedQuantity);


    const handleTotalLowestPriceSum = (sum) => {
        setTotalLowestPriceSum(sum);
    };
    const handleTotalLowesLabourtPriceSum = (sum) => {
        setTotalLowestLabourPriceSum(sum);
    };
    const handleOtherTotalLowestPriceSum = (sum) => {
        setOtherstotalLowestPriceSum(sum);
    };

    const handleSectionClick = (rfqId, rfqType) => {
        navigate(`/full-comparison/&rfqId=${rfqId}/rfqType=${rfqType}`, { state: { rfqData: { ...data, suppliers: suppliersWithQuotation } } })
    }
    const navigate = useNavigate();

    const handleMaterialDropdown = (rowIndex, vendorIndex) => {
        if (openDropdown.rowIndex === rowIndex && openDropdown.vendorIndex === vendorIndex) {
            setOpenDropdown({ rowIndex: null, vendorIndex: null });
        } else {
            setOpenDropdown({ rowIndex, vendorIndex });
        }
    };

    const handleTransportationClicked = (index, e) => {
        e.stopPropagation();
        setOpenNoteIndex(openNoteIndex === index ? null : index);
    };

    const handleGstChange = (e) => {
        setWithGst(!withGst);
    };

    const hasScopeMaterials = (scope) => Array.isArray(scope?.materials) && scope.materials.length > 0;

    const getScopeUnitRate = (scope) => {
        const quantity = parseNumber(scope?.quantity);
        const total = parseNumber(scope?.totalExclGst);
        return quantity > 0 ? total / quantity : total;
    };

    const findContractorVendorScope = (supplier, sectionId, scopeId) => {
        const latestSet = supplier?.vendorQuotations?.quotationSets?.slice(-1)[0];
        const section = latestSet?.contractorDetails?.find(item => String(item?.id) === String(sectionId));
        return section?.scopes?.find(item => String(item?.id) === String(scopeId));
    };

    const buildContractorScopeEntry = (section, scope, vendorScope) => {
        const isMaterialBacked = hasScopeMaterials(scope) || hasScopeMaterials(vendorScope);
        const quantity = parseNumber(scope?.quantity || vendorScope?.quantity || 1, 1);
        const selectedScopeRate = isMaterialBacked
            ? parseNumber(vendorScope?.totalExclGst)
            : getScopeUnitRate(vendorScope);

        return {
            sectionId: section?._id,
            sectionName: section?.sectionName,
            scopeId: scope?._id,
            scopeOfWork: scope?.scopeOfWork,
            quantity: scope?.quantity ?? vendorScope?.quantity,
            selectedQuantity: quantity,
            selectedScopeRate,
            selectedSupplierPrice: isMaterialBacked ? parseNumber(vendorScope?.totalExclGst) : selectedScopeRate * quantity,
            isMaterialBacked
        };
    };

    const hasSelectionEntries = (selectionMap) =>
        Object.values(selectionMap || {}).some(items => (items?.length || 0) > 0);

    const hasSavedContractorSelection = (supplier) =>
        getLatestQuotationSet(supplier)?.contractorDetails?.some(section =>
            section?.scopes?.some(scope =>
                scope?.isSelected === true
            )
        );

    const buildLegacyAwardedContractorSelectionMap = () => {
        const nextMap = {};

        sortedSuppliers?.forEach((supplier) => {
            if (!supplier?.isAwarded || hasSavedContractorSelection(supplier)) return;

            const selectedScopes = [];
            (rfqData?.providingFixingDetails || []).forEach((section) => {
                (section?.scopes || []).forEach((scope) => {
                    const vendorScope = findContractorVendorScope(supplier, section?._id, scope?._id);
                    if (!vendorScope) return;
                    selectedScopes.push(buildContractorScopeEntry(section, scope, vendorScope));
                });
            });

            if (selectedScopes.length) {
                nextMap[supplier.supplierId] = selectedScopes;
            }
        });

        return nextMap;
    };

    // Legacy closed contractor RFQs do not have split-selection fields, so display the awarded vendor as fully selected.
    const effectiveSelectedMaterialsMap = useMemo(() => {
        const hasExplicitSelection = hasSelectionEntries(selectedMaterialsMap);
        const shouldUseLegacyContractorFallback =
            rfqData?.rfqType === "contractor" &&
            selectedRatesTab &&
            rfqData?.status === "CLOSED" &&
            !hasExplicitSelection &&
            sortedSuppliers?.some(supplier => supplier?.isAwarded) &&
            !sortedSuppliers?.some(hasSavedContractorSelection);

        return shouldUseLegacyContractorFallback
            ? buildLegacyAwardedContractorSelectionMap()
            : selectedMaterialsMap;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedMaterialsMap, selectedRatesTab, rfqData?.rfqType, rfqData?.status, rfqData?.providingFixingDetails, sortedSuppliers]);

    const hasAnySelectedItems = hasSelectionEntries(effectiveSelectedMaterialsMap);
    const showSelectedValueColumn = canSelectRates && hasAnySelectedItems;

    useLayoutEffect(() => {
        const table = comparisonTableRef.current;
        if (!table) return undefined;

        const requiresSharedHeaderHeight =
            sortedSuppliers.length > 0 && collapsedVendorIds.size < sortedSuppliers.length;

        if (!requiresSharedHeaderHeight) {
            table.style.removeProperty("--comparison-header-height");
            setComparisonHeaderHeight(null);
            return undefined;
        }

        const measureHeaderHeight = () => {
            table.style.removeProperty("--comparison-header-height");

            const headerCells = Array.from(
                table.querySelectorAll("thead > tr > th.comparison-header-cell")
            ).filter(cell => window.getComputedStyle(cell).display !== "none");

            const measuredHeight = Math.ceil(
                Math.max(...headerCells.map(cell => cell.getBoundingClientRect().height), 0)
            );

            if (measuredHeight > 0) {
                setComparisonHeaderHeight(previousHeight => {
                    if (previousHeight === measuredHeight) {
                        table.style.setProperty("--comparison-header-height", `${measuredHeight}px`);
                        return previousHeight;
                    }

                    return measuredHeight;
                });
            }
        };

        let animationFrameId = window.requestAnimationFrame(measureHeaderHeight);

        const scheduleMeasure = () => {
            window.cancelAnimationFrame(animationFrameId);
            animationFrameId = window.requestAnimationFrame(measureHeaderHeight);
        };

        window.addEventListener("resize", scheduleMeasure);

        return () => {
            window.cancelAnimationFrame(animationFrameId);
            window.removeEventListener("resize", scheduleMeasure);
        };
    }, [
        closeRfqForm,
        selectedRatesTab,
        rfqData?.rfqType,
        showSelectedValueColumn,
        sortedSuppliers.length,
        collapsedVendorIds,
        totalSelectedQuotationValue,
        totalLowestPriceSum,
        totalLowestLabourPriceSum,
        otherstotalLowestPriceSum,
    ]);

    useLayoutEffect(() => {
        if (!openVisibilityMenu) {
            setVisibilityMenuPosition(null);
            return undefined;
        }

        const anchorRef = openVisibilityMenu === "rows"
            ? hiddenRowsTriggerRef
            : hiddenVendorsTriggerRef;

        const updatePosition = () => {
            const anchor = anchorRef.current;
            if (!anchor) return;

            const rect = anchor.getBoundingClientRect();
            const menuWidth = 250;
            setVisibilityMenuPosition({
                top: rect.bottom + 4,
                left: Math.max(8, Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - 8)),
            });
        };

        const handleOutsideClick = (event) => {
            if (
                !anchorRef.current?.contains(event.target) &&
                !visibilityMenuRef.current?.contains(event.target)
            ) {
                setOpenVisibilityMenu(null);
            }
        };

        updatePosition();
        window.addEventListener("resize", updatePosition);
        document.addEventListener("scroll", updatePosition, true);
        document.addEventListener("mousedown", handleOutsideClick);

        return () => {
            window.removeEventListener("resize", updatePosition);
            document.removeEventListener("scroll", updatePosition, true);
            document.removeEventListener("mousedown", handleOutsideClick);
        };
    }, [openVisibilityMenu]);

    const getContractorVendorSelectionEntries = (supplierId) =>
        effectiveSelectedMaterialsMap?.[supplierId] || [];

    const isAllVendorContractorScopesSelected = (vendorId) => {
        const vendorScopes = getContractorVendorSelectionEntries(vendorId);
        const selectedScopeIds = new Set(vendorScopes.map(item => String(item.scopeId)));
        const rfqScopes = (rfqData?.providingFixingDetails || []).flatMap(section => section?.scopes || []);

        return rfqScopes.length > 0 && rfqScopes.every(scope => selectedScopeIds.has(String(scope?._id)));
    };

    const handleContractorVendorCheckboxChange = (vendorId) => {
        const vendor = sortedSuppliers.find(supplier => String(supplier.supplierId) === String(vendorId));
        if (!vendor || !rfqData?.providingFixingDetails?.length) return;

        if (isAllVendorContractorScopesSelected(vendorId)) {
            setSelectedMaterialsMap({});
            setSelectedVendors([]);
            setTotalSelectedQuotationValue(0);
            return;
        }

        const selectedScopes = [];
        rfqData.providingFixingDetails.forEach((section) => {
            (section?.scopes || []).forEach((scope) => {
                const vendorScope = findContractorVendorScope(vendor, section?._id, scope?._id);
                if (!vendorScope) return;
                selectedScopes.push(buildContractorScopeEntry(section, scope, vendorScope));
            });
        });

        const nextMap = selectedScopes.length ? { [vendorId]: selectedScopes } : {};
        setSelectedMaterialsMap(nextMap);
        setSelectedVendors(Object.keys(nextMap));
        setTotalSelectedQuotationValue(selectedScopes.reduce((sum, item) => sum + parseNumber(item.selectedSupplierPrice), 0));
    };

    const getEnabledChargeTypes = () =>
        Object.keys(rfqData?.otherCharges || {}).filter(type => rfqData?.otherCharges?.[type]);

    const getContractorVendorExtraCharges = (supplier) => {
        const supplierId = supplier?.supplierId;
        const selectedItems = getContractorVendorSelectionEntries(supplierId);
        if (!selectedItems.length) return 0;

        const editedCharges = chargeInputs?.[supplierId] || {};
        if (Object.keys(editedCharges).length > 0) {
            return Object.values(editedCharges).reduce((sum, value) => sum + parseNumber(value), 0);
        }

        const vendorQuotationSet = getLatestQuotationSet(supplier);
        return parseNumber(vendorQuotationSet?.selectedOthersTotalWithoutGst ?? vendorQuotationSet?.othersTotalWithoutGst);
    };

    const getMaterialVendorSelectionEntries = (supplierId) =>
        effectiveSelectedMaterialsMap?.[supplierId] || [];

    const getMaterialVendorSelectedAmount = (supplierId) =>
        getMaterialVendorSelectionEntries(supplierId).reduce(
            (sum, item) => sum + (Number(item.selectedSupplierPrice) || 0),
            0
        );

    const getMaterialVendorExtraCharges = (supplier) => {
        const supplierId = supplier?.supplierId;
        const selectedItems = getMaterialVendorSelectionEntries(supplierId);
        if (!selectedItems.length) return 0;

        const vendorQuotationSet = supplier?.vendorQuotations?.quotationSets?.slice(-1)[0];
        const otherCharges = vendorQuotationSet?.otherCharges || {};

        return getEnabledChargeTypes()
            .filter(chargeType => !isAdvancedTransport || chargeType.toLowerCase() !== "transport")
            .reduce((sum, chargeType) => {
                const editedValue = chargeInputs?.[supplierId]?.[chargeType];
                const baseValue = selectedRatesTab
                    ? (otherCharges?.[chargeType]?.selectedMrp ?? otherCharges?.[chargeType]?.mrp ?? 0)
                    : (otherCharges?.[chargeType]?.mrp ?? 0);
                const chargeAmount =
                    editedValue !== undefined && editedValue !== null && editedValue !== ""
                        ? Number(editedValue) || 0
                        : Number(baseValue) || 0;
                return sum + chargeAmount;
            }, 0);
    };

    const getMaterialVendorGstAmount = (supplier) => {
        const supplierId = supplier?.supplierId;
        const selectedItems = getMaterialVendorSelectionEntries(supplierId);
        if (!selectedItems.length) return 0;

        const vendorQuotationSet = supplier?.vendorQuotations?.quotationSets?.slice(-1)[0];
        const materialDetails = vendorQuotationSet?.materialDetails || [];
        const otherCharges = vendorQuotationSet?.otherCharges || {};

        const selectedMaterialAmount = getMaterialVendorSelectedAmount(supplierId);
        const discountedMaterialAmount = getMaterialVendorDiscountedSelectedAmount(supplierId, selectedMaterialAmount, supplier);
        const discountRatio = selectedMaterialAmount > 0 ? discountedMaterialAmount / selectedMaterialAmount : 1;

        const materialGst = selectedItems.reduce((sum, item) => {
            const vendorItem = materialDetails.find(material => material.id === item.materialId);
            const gstPercent = Number(vendorItem?.gst) || 0;
            const discountedLineAmount = (Number(item.selectedSupplierPrice) || 0) * discountRatio;
            return sum + (discountedLineAmount * gstPercent) / 100;
        }, 0);

        const extraChargesGst = getEnabledChargeTypes()
            .filter(chargeType => !isAdvancedTransport || chargeType.toLowerCase() !== "transport")
            .reduce((sum, chargeType) => {
                const editedValue = chargeInputs?.[supplierId]?.[chargeType];
                const baseValue = selectedRatesTab
                    ? (otherCharges?.[chargeType]?.selectedMrp ?? otherCharges?.[chargeType]?.mrp ?? 0)
                    : (otherCharges?.[chargeType]?.mrp ?? 0);
                const chargeAmount =
                    editedValue !== undefined && editedValue !== null && editedValue !== ""
                        ? Number(editedValue) || 0
                        : Number(baseValue) || 0;
                const gstPercent = Number(otherCharges?.[chargeType]?.gst) || 0;
                return sum + (chargeAmount * gstPercent) / 100;
            }, 0);

        return materialGst + extraChargesGst;
    };

    const getMaterialVendorSelectedTotalWithoutGst = (supplier) => {
        const selectedAmount = getMaterialVendorSelectedAmount(supplier?.supplierId);
        if (!selectedAmount) return 0;
        return getMaterialVendorDiscountedSelectedAmount(supplier?.supplierId, selectedAmount, supplier) + getMaterialVendorExtraCharges(supplier);
    };

    const getMaterialVendorSelectedTotalWithGst = (supplier) => {
        const totalWithoutGst = getMaterialVendorSelectedTotalWithoutGst(supplier);
        if (!totalWithoutGst) return 0;
        return totalWithoutGst + getMaterialVendorGstAmount(supplier);
    };

    const getLabourVendorSelectionEntries = (supplierId) =>
        effectiveSelectedMaterialsMap?.[supplierId] || [];

    const getLabourVendorSelectedAmount = (supplierId) =>
        getLabourVendorSelectionEntries(supplierId).reduce(
            (sum, item) => sum + (Number(item.selectedSupplierPrice) || 0),
            0
        );

    const getLabourVendorExtraCharges = (supplier) => {
        const supplierId = supplier?.supplierId;
        const selectedItems = getLabourVendorSelectionEntries(supplierId);
        if (!selectedItems.length) return 0;

        const vendorQuotationSet = supplier?.vendorQuotations?.quotationSets?.slice(-1)[0];
        const otherCharges = vendorQuotationSet?.otherCharges || {};

        return getEnabledChargeTypes()
            .reduce((sum, chargeType) => {
                const editedValue = chargeInputs?.[supplierId]?.[chargeType];
                const baseValue = selectedRatesTab
                    ? (otherCharges?.[chargeType]?.selectedMrp ?? otherCharges?.[chargeType]?.mrp ?? 0)
                    : (otherCharges?.[chargeType]?.mrp ?? 0);
                const chargeAmount =
                    editedValue !== undefined && editedValue !== null && editedValue !== ""
                        ? Number(editedValue) || 0
                        : Number(baseValue) || 0;
                return sum + chargeAmount;
            }, 0);
    };

    const getLabourVendorGstAmount = (supplier) => {
        const supplierId = supplier?.supplierId;
        const selectedItems = getLabourVendorSelectionEntries(supplierId);
        if (!selectedItems.length) return 0;

        const vendorQuotationSet = supplier?.vendorQuotations?.quotationSets?.slice(-1)[0];
        const labourItems = vendorQuotationSet?.labourItems || [];
        const otherCharges = vendorQuotationSet?.otherCharges || {};

        const labourGst = selectedItems.reduce((sum, item) => {
            const vendorItem = labourItems.find(labour => labour.id === item.labourId);
            const gstPercent = Number(vendorItem?.gst) || 0;
            return sum + ((Number(item.selectedSupplierPrice) || 0) * gstPercent) / 100;
        }, 0);

        const extraChargesGst = getEnabledChargeTypes()
            .reduce((sum, chargeType) => {
                const editedValue = chargeInputs?.[supplierId]?.[chargeType];
                const baseValue = selectedRatesTab
                    ? (otherCharges?.[chargeType]?.selectedMrp ?? otherCharges?.[chargeType]?.mrp ?? 0)
                    : (otherCharges?.[chargeType]?.mrp ?? 0);
                const chargeAmount =
                    editedValue !== undefined && editedValue !== null && editedValue !== ""
                        ? Number(editedValue) || 0
                        : Number(baseValue) || 0;
                const gstPercent = Number(otherCharges?.[chargeType]?.gst) || 0;
                return sum + (chargeAmount * gstPercent) / 100;
            }, 0);

        return labourGst + extraChargesGst;
    };

    const getLabourVendorSelectedTotalWithoutGst = (supplier) => {
        const selectedAmount = getLabourVendorSelectedAmount(supplier?.supplierId);
        if (!selectedAmount) return 0;
        return selectedAmount + getLabourVendorExtraCharges(supplier);
    };

    const getLabourVendorSelectedTotalWithGst = (supplier) => {
        const totalWithoutGst = getLabourVendorSelectedTotalWithoutGst(supplier);
        if (!totalWithoutGst) return 0;
        return totalWithoutGst + getLabourVendorGstAmount(supplier);
    };

    const getContractorVendorSelectedAmount = (supplierId) =>
        (effectiveSelectedMaterialsMap?.[supplierId] || []).reduce(
            (sum, item) => sum + (Number(item.selectedSupplierPrice) || 0),
            0
        );


    const getInitials = (name) => {
        if (!name) return '';
        const names = name.split(' ');
        // Get the first four initials
        return names.slice(0, 4).map(name => name.charAt(0).toUpperCase()).join('');
    };


    useEffect(() => {
        const getRankValue = (supplier) => {
            const rank = withGst
                ? supplier.vendorQuotations?.rankWithGst
                : supplier.vendorQuotations?.rank;

            // NA or missing rank should go to bottom
            if (!rank || rank === 'NA') return Number.POSITIVE_INFINITY;

            // "L1", "L2" → extract number
            const parsed = parseInt(rank?.replace('L', ''), 10);
            return isNaN(parsed) ? Number.POSITIVE_INFINITY : parsed;
        };

        const filteredAndSorted = rfqData?.suppliers
            .filter(supplier => supplier?.quotationReceived && !isSupplierExcludedFromComparison(supplier))
            .sort((a, b) => getRankValue(a) - getRankValue(b));

        setSortedSuppliers(filteredAndSorted);
    }, [rfqData, withGst]);


    const handleExcludeFromComparison = async (supplier, reason) => {
        if (!supplier?.supplierId) return;
        try {
            await dispatch(updateRFQComparisonExclusion({
                rfqId: rfqData?.rfqId,
                supplierId: supplier.supplierId,
                excluded: true,
                reason,
                entityId: rfqData?.entityId,
                actionBy: {
                    userId: userProfile?.userId,
                    name: userProfile?.name,
                    email: userProfile?.email,
                },
            }));
            enqueueSnackbar("Vendor excluded from comparison", { variant: "success" });
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to exclude vendor from comparison", { variant: "error" });
            throw error;
        }
    };


    const handleRequestQuotationUpdate = async (supplier, managerMessage = "") => {
        if (!supplier?.supplierId) return;
        try {
            await dispatch(requestRFQQuotationUpdate({
                rfqId: rfqData?.rfqId,
                supplierId: supplier.supplierId,
                entityId: rfqData?.entityId,
                managerMessage,
                actionBy: {
                    userId: userProfile?.userId,
                    name: userProfile?.name,
                    email: userProfile?.email,
                },
            }));
            enqueueSnackbar("Quotation update request sent", { variant: "success" });
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to request quotation update", { variant: "error" });
            throw error;
        }
    };
    const getL1SupplierData = () => {
        const l1Supplier = sortedSuppliers[0];
        return l1Supplier ? l1Supplier.vendorQuotations?.quotationSets?.slice(-1)[0] : null;
    };

    useEffect(() => {
        let totalSum = sortedSuppliers?.reduce((sum, supplier) => {
            const selectedMaterials = effectiveSelectedMaterialsMap?.[supplier?.supplierId] || []; // Array of selected materials

            // Calculate the total for selected materials
            const selectedMaterialBasePriceSum = selectedMaterials.reduce((total, material) => {
                return total + (parseFloat(material.selectedSupplierPrice) || 0);
            }, 0);
            const selectedMaterialAmountAfterDiscount = getMaterialVendorDiscountedSelectedAmount(
                supplier.supplierId,
                selectedMaterialBasePriceSum,
                supplier
            );

            // Get charge inputs for the current supplier
            const supplierCharges = chargeInputs[supplier.supplierId] || {};

            // Sum up all charges except "transport" if isAdvancedTransport is true
            const totalExtraCharges = Object.entries(supplierCharges)
                .filter(([chargeType]) => !isAdvancedTransport || chargeType.toLowerCase() !== "transport")
                .reduce((total, [, chargeValue]) => total + (parseFloat(chargeValue) || 0), 0);

            // Only add extra charges if at least one material is selected
            const finalSelectedTotal = selectedMaterials.length > 0
                ? selectedMaterialAmountAfterDiscount + (
                    rfqData?.rfqType === "contractor"
                        ? getContractorVendorExtraCharges(supplier)
                        : totalExtraCharges
                )
                : selectedMaterialAmountAfterDiscount;

            return sum + finalSelectedTotal;
        }, 0);

        setTotalSelectedQuotationValue(totalSum);
    }, [effectiveSelectedMaterialsMap, chargeInputs, discountOnBasicAmountInputs, isAdvancedTransport, sortedSuppliers]);

    useEffect(() => {
        if (!sortedSuppliers?.length || data?.rfqType !== "contractor") return;
        if (selectedRatesTab) return;
        if (Object.values(selectedMaterialsMap || {}).some(items => (items || []).length > 0)) return;

        const awardedSupplier = sortedSuppliers.find(s => s.isAwarded);
        if (!awardedSupplier) return;

        handleContractorVendorCheckboxChange(awardedSupplier.supplierId);
    }, [sortedSuppliers, data?.rfqType, selectedRatesTab]);

    useEffect(() => {
        if (rfqData?.rfqType !== "contractor") return;

        let total = 0;

        rfqData?.providingFixingDetails?.forEach((section) => {
            const lowestForSection = Math.min(
                ...sortedSuppliers.map((supplier) => {
                    const quotation =
                        supplier.vendorQuotations?.quotationSets?.slice(-1)[0];

                    const contractorDetails = quotation?.contractorDetails || [];

                    const matchingSection = contractorDetails.find(
                        (item) =>
                            item.id === section._id ||
                            item.sectionName === section.sectionName
                    );

                    return matchingSection?.sectionTotalExclGst ?? Infinity;
                })
            );

            if (lowestForSection !== Infinity) {
                total += lowestForSection;
            }
        });

        setTotalLowestContractorPriceSum(total);
    }, [rfqData?.rfqType, rfqData?.providingFixingDetails, sortedSuppliers]);

    const l1Data = getL1SupplierData();
    const l1QuotationValue = sortedSuppliers[0]?.vendorQuotations?.grandTotalWithoutGst;
    const differencefromL1ToSelected = l1QuotationValue - totalSelectedQuotationValue;

    const calculateLowerCount = (supplier, l1Data) => {
        let lowerCount = 0;

        if (l1Data) {
            // Handle materials

            if (rfqData.rfqType === 'material') {
                const l1MaterialPrices = l1Data.materialDetails.reduce((acc, item) => {
                    acc[item.name] = item.basePrice;
                    return acc;
                }, {});

                supplier.vendorQuotations?.quotationSets?.slice(-1)[0]?.materialDetails.forEach(item => {
                    const l1Price = l1MaterialPrices[item.name];
                    const supplierPrice = item.basePrice;
                    if (l1Price && supplierPrice < l1Price) {
                        lowerCount++;
                    }
                });
            }

            // Handle labourItems if rfqType is 'labour'
            if (rfqData.rfqType === 'labour') {
                const l1LabourRates = l1Data.labourItems.reduce((acc, item) => {
                    acc[item.skill] = item.labourRate;
                    return acc;
                }, {});

                supplier.vendorQuotations?.quotationSets?.slice(-1)[0]?.labourItems.forEach(item => {
                    const l1Rate = l1LabourRates[item.skill];
                    const supplierRate = item.labourRate;
                    if (l1Rate && supplierRate < l1Rate) {
                        lowerCount++;
                    }
                });
            }

            // Handle contractorDetails if rfqType is 'contractor'
            if (rfqData.rfqType === 'contractor') {
                const l1ContractorRates = l1Data.contractorDetails;

                const supplierContractorDetails = supplier.vendorQuotations?.quotationSets?.slice(-1)[0]?.contractorDetails;
                if (supplierContractorDetails) {
                    const supplierRate = supplierContractorDetails.baseRate;
                    const l1Rate = l1ContractorRates.baseRate;
                    if (l1Rate && supplierRate < l1Rate) {
                        lowerCount++;
                    }
                }
            }
        }

        return lowerCount;
    };

    // Check if all materials are selected for a specific vendor
    const isAllVendorMaterialsSelected = (vendorId) => {
        if (!rfqData?.materials?.length) return false;

        const vendorMaterials = selectedMaterialsMap[vendorId] || [];

        // Build set of selected materialIds for this vendor
        const selectedIds = new Set(
            vendorMaterials.map(item => item.materialId)
        );

        // Check every material exists AND is assigned to this vendor
        return rfqData.materials.every(material =>
            selectedIds.has(material._id)
        );
    };

    // Handle vendor checkbox change (select/deselect all materials for this vendor)
    const handleVendorCheckboxChange = (vendorId) => {
        const vendor = sortedSuppliers.find(s => s.supplierId === vendorId);
        if (!vendor || !rfqData?.materials?.length) return;

        const vendorQuotation = vendor.vendorQuotations?.quotationSets?.slice(-1)[0];
        if (!vendorQuotation) return;

        const isCurrentlySelected = isAllVendorMaterialsSelected(vendorId);

        if (isCurrentlySelected) {
            // Deselect all materials for this vendor
            setSelectedMaterialsMap(prev => {
                const updated = { ...prev };
                delete updated[vendorId];
                return updated;
            });

            // Update selected checkboxes
            setSelectedCheckboxes(prev => {
                const updated = { ...prev };
                rfqData.materials.forEach(material => {
                    if (updated[material._id]) {
                        updated[material._id] = updated[material._id].filter(id => id !== vendorId);
                    }
                });
                return updated;
            });
        } else {
            // Select all materials for this vendor
            const newVendorMaterials = {};
            const newSelectedCheckboxes = { ...selectedCheckboxes };
            const existingVendorSelections = selectedMaterialsMap[vendorId] || [];
            let skippedFullQuantityMaterials = [];

            rfqData.materials.forEach(material => {
                const vendorItem = vendorQuotation.materialDetails?.find(item => String(item.id) === String(material._id));

                if (vendorItem) {
                    const existingSelection = existingVendorSelections.find(
                        item => String(item.materialId) === String(material._id)
                    );
                    const availableQuantity = parseNumber(material.quantity) - getSelectedQuantityForMaterial(material._id, vendorId);
                    const selectedQuantity = parseNumber(existingSelection?.selectedQuantity) > 0
                        ? parseNumber(existingSelection.selectedQuantity)
                        : availableQuantity;

                    if (selectedQuantity <= 0) {
                        skippedFullQuantityMaterials.push(material?.name || "material");
                        return;
                    }

                    const selectedDiscount = existingSelection?.selectedDiscount ?? vendorItem.discount ?? 0;
                    const selectedTransportRate = existingSelection?.selectedTransportRate ?? vendorItem.transportRate ?? 0;
                    const selectedItemRate = existingSelection?.selectedItemRate ??
                        (parseNumber(vendorItem.basePrice) - (parseNumber(vendorItem.basePrice) * parseNumber(selectedDiscount)) / 100);
                    const selectedTransportCost = calculateSelectedTransportCost(
                        selectedTransportRate,
                        selectedQuantity,
                        existingSelection?.selectedTransportCost ?? vendorItem.transportCost ?? 0
                    );

                    // Add to vendor materials map
                    if (!newVendorMaterials[vendorId]) {
                        newVendorMaterials[vendorId] = [];
                    }

                    newVendorMaterials[vendorId].push({
                        materialId: material._id,
                        selectedQuantity,
                        selectedDiscount,
                        selectedTransportRate,
                        selectedTransportCost,
                        selectedItemRate,
                        selectedSupplierPrice: calculateSelectedSupplierPrice(
                            selectedItemRate,
                            selectedQuantity,
                            selectedTransportCost
                        )
                    });

                    // Add to selected checkboxes
                    if (!newSelectedCheckboxes[material._id]) {
                        newSelectedCheckboxes[material._id] = [];
                    }

                    if (!ALLOW_MULTI_VENDOR_PER_MATERIAL) {
                        // Single vendor mode (temporary)
                        newSelectedCheckboxes[material._id] = [vendorId];
                    } else {
                        // Original multi-select logic
                        if (!newSelectedCheckboxes[material._id].includes(vendorId)) {
                            newSelectedCheckboxes[material._id].push(vendorId);
                        }
                    }
                }
            });

            if (skippedFullQuantityMaterials.length === rfqData.materials.length) {
                enqueueSnackbar("Full RFQ quantity is already allocated for all materials.", {
                    variant: "warning",
                    autoHideDuration: 4000,
                });
                return;
            }

            if (skippedFullQuantityMaterials.length > 0) {
                enqueueSnackbar("Some materials were skipped because their full RFQ quantity is already allocated.", {
                    variant: "warning",
                    autoHideDuration: 4000,
                });
            }

            // Update state
            if (!ALLOW_MULTI_VENDOR_PER_MATERIAL) {
                // SINGLE MODE → Replace everything
                setSelectedMaterialsMap(newVendorMaterials);
            } else {
                // MULTI MODE → Keep original behaviour
                setSelectedMaterialsMap(prev => ({
                    ...prev,
                    ...newVendorMaterials
                }));
            }

            setSelectedCheckboxes(newSelectedCheckboxes);
        }
    };

    const isAllVendorLaboursSelected = (vendorId) => {
        if (!rfqData?.labourDetails?.length) return false;

        const vendorLabours = selectedMaterialsMap[vendorId] || [];
        const selectedIds = new Set(vendorLabours.map(item => String(item.labourId)));

        return rfqData.labourDetails.every(labour =>
            selectedIds.has(String(labour._id))
        );
    };

    const handleVendorLabourCheckboxChange = (vendorId) => {
        const vendor = sortedSuppliers.find(s => s.supplierId === vendorId);
        if (!vendor || !rfqData?.labourDetails?.length) return;

        const vendorQuotation = vendor.vendorQuotations?.quotationSets?.slice(-1)[0];
        if (!vendorQuotation) return;

        if (isAllVendorLaboursSelected(vendorId)) {
            setSelectedMaterialsMap(prev => {
                const updated = { ...prev };
                delete updated[vendorId];
                return updated;
            });
            return;
        }

        const selectedLabours = [];

        rfqData.labourDetails.forEach(labour => {
            const vendorItem = vendorQuotation.labourItems?.find(item => String(item.id) === String(labour._id));
            if (!vendorItem) return;

            const selectedQuantity = parseNumber(labour.quantity);
            const selectedLabourRate = vendorItem.labourRate ?? 0;

            selectedLabours.push({
                labourId: labour._id,
                quantity: labour.quantity,
                selectedQuantity,
                selectedLabourRate,
                selectedSupplierPrice: calculateSelectedLabourPrice(selectedLabourRate, selectedQuantity),
            });
        });

        setSelectedMaterialsMap(prev => {
            const rfqLabourIds = new Set((rfqData.labourDetails || []).map(labour => String(labour._id)));
            const updated = Object.entries(prev || {}).reduce((acc, [supplierId, labourItems]) => {
                const remainingLabours = (labourItems || []).filter(
                    item => !rfqLabourIds.has(String(item.labourId))
                );

                if (remainingLabours.length) {
                    acc[supplierId] = remainingLabours;
                }

                return acc;
            }, {});

            updated[vendorId] = selectedLabours;
            return updated;
        });
    };

    const hasAnyMaterialSelected =
        Object.values(effectiveSelectedMaterialsMap || {}).some(
            (materials) => materials?.length > 0
        );

    const collapsedVendorIndexes = useMemo(
        () => sortedSuppliers.reduce((indexes, supplier, index) => {
            if (collapsedVendorIds.has(String(supplier?.supplierId))) indexes.push(index);
            return indexes;
        }, []),
        [collapsedVendorIds, sortedSuppliers]
    );

    const collapsedVendorTableClasses = collapsedVendorIndexes
        .map(index => `comparison-vendor-column-${index}-collapsed`)
        .join(" ");

    const collapsedVendorColumnStyles = collapsedVendorIndexes.map((index) => {
        const columnPosition = index + 2;
        return `
            .comparison-table.comparison-vendor-column-${index}-collapsed thead > tr > th:nth-child(${columnPosition}),
            .comparison-table.comparison-vendor-column-${index}-collapsed tbody td:nth-child(${columnPosition}) {
                display: none !important;
            }
        `;
    }).join("\n");

    const toggleVendorColumn = (supplierId) => {
        const normalizedSupplierId = String(supplierId);
        setCollapsedVendorIds((previous) => {
            const next = new Set(previous);
            if (next.has(normalizedSupplierId)) {
                next.delete(normalizedSupplierId);
            } else {
                next.add(normalizedSupplierId);
            }
            return next;
        });
    };

    const expandAllVendorColumns = () => setCollapsedVendorIds(new Set());

    const collapseAllVendorColumns = () => {
        setCollapsedVendorIds(
            new Set(sortedSuppliers.map(supplier => String(supplier?.supplierId)))
        );
    };

    const toggleLineItemVisibility = (lineItemKey) => {
        setHiddenLineItemKeys((previous) => {
            const next = new Set(previous);
            if (next.has(lineItemKey)) next.delete(lineItemKey);
            else next.add(lineItemKey);
            return next;
        });
    };

    const areAllLineItemsHidden =
        comparisonLineItems.length > 0 && hiddenLineItems.length === comparisonLineItems.length;

    const toggleAllLineItems = () => {
        setHiddenLineItemKeys(
            areAllLineItemsHidden
                ? new Set()
                : new Set(comparisonLineItems.map(item => item.key))
        );
    };

    const showAllLineItems = () => setHiddenLineItemKeys(new Set());

    const renderLineItemHideButton = (lineItemKey, lineItemLabel) => (
        <button
            type="button"
            onClick={(event) => {
                event.stopPropagation();
                toggleLineItemVisibility(lineItemKey);
            }}
            aria-label={`Hide ${lineItemLabel}`}
            title="Hide line item"
            className="flex h-5 w-5 shrink-0 items-center justify-center rounded bg-transparent text-slate-700 transition hover:bg-blue-50 hover:text-blue-700 focus:outline-none focus:ring-1 focus:ring-blue-200"
        >
            <VisibilityOffIcon aria-hidden="true" style={{ fontSize: 16 }} />
        </button>
    );

    const renderHiddenLineItemControls = () => hiddenLineItems.length > 0 && (
        <div className="group relative">
            <button
                ref={hiddenRowsTriggerRef}
                type="button"
                onClick={() => setOpenVisibilityMenu(current => current === "rows" ? null : "rows")}
                aria-haspopup="true"
                aria-expanded={openVisibilityMenu === "rows"}
                aria-label={`${hiddenLineItems.length} hidden line items`}
                title="Show hidden line items"
                className="mb-2 flex h-6 items-center gap-1 border border-[#767676] bg-white px-1.5 text-[10px] font-semibold text-gray-700 transition hover:border-blue-500 hover:text-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-200"
            >
                <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-slate-100 px-1 text-[9px] font-bold text-slate-600">
                    {hiddenLineItems.length}
                </span>
                <span>Hidden rows</span>
                <ChevronRightIcon
                    aria-hidden="true"
                    style={{ fontSize: 14, transform: "rotate(90deg)" }}
                />
            </button>

            <div className="hidden">
                <div className="flex items-center justify-between gap-2 px-2 py-1">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                        Show a line item
                    </p>
                    <button
                        type="button"
                        onClick={showAllLineItems}
                        className="shrink-0 rounded border border-blue-200 bg-blue-50 px-2 py-0.5 text-[10px] font-semibold text-blue-700 transition hover:border-blue-300 hover:bg-blue-100 focus:outline-none focus:ring-1 focus:ring-blue-200"
                    >
                        Show all
                    </button>
                </div>

                <div className="max-h-56 overflow-y-auto">
                    {hiddenLineItems.map((item) => (
                        <button
                            key={item.key}
                            type="button"
                            onClick={() => toggleLineItemVisibility(item.key)}
                            className="flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left transition hover:bg-slate-50 focus:bg-slate-50 focus:outline-none"
                        >
                            <span className="min-w-0 truncate text-[10px] font-semibold text-gray-700">
                                {item.serialNumber}. {item.label}
                            </span>
                            <VisibilityIcon className="shrink-0" aria-hidden="true" style={{ fontSize: 15 }} />
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );

    const renderVendorVisibilityControls = () => (
        <div className="flex shrink-0 items-center gap-1">
            {collapsedVendorIndexes.length > 0 && (
                <div className="group relative">
                    <button
                        ref={hiddenVendorsTriggerRef}
                        type="button"
                        onClick={() => setOpenVisibilityMenu(current => current === "vendors" ? null : "vendors")}
                        aria-haspopup="true"
                        aria-expanded={openVisibilityMenu === "vendors"}
                        aria-label={`${collapsedVendorIndexes.length} hidden vendors`}
                        title="Show hidden vendors"
                        className="mb-2 flex h-6 items-center gap-1 border-[1px] border-[#767676] bg-white px-1.5 text-[10px] font-semibold text-gray-700 transition hover:border-blue-500 hover:text-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-200"
                    >
                        <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-slate-100 px-1 text-[9px] font-bold text-slate-600">
                            {collapsedVendorIndexes.length}
                        </span>
                        <span>Vendors</span>
                        <ChevronRightIcon
                            aria-hidden="true"
                            style={{ fontSize: 14, transform: "rotate(90deg)" }}
                        />
                    </button>

                    <div className="hidden">
                        <div className="flex items-center justify-between gap-2 px-2 py-1">
                            <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                                Show a vendor
                            </p>
                            <button
                                type="button"
                                onClick={expandAllVendorColumns}
                                className="shrink-0 rounded border border-blue-200 bg-blue-50 px-2 py-0.5 text-[10px] font-semibold text-blue-700 transition hover:border-blue-300 hover:bg-blue-100 focus:outline-none focus:ring-1 focus:ring-blue-200"
                            >
                                Show all
                            </button>
                        </div>

                        {sortedSuppliers
                            .filter(supplier => collapsedVendorIds.has(String(supplier?.supplierId)))
                            .map(supplier => {
                                const hiddenQuotedTotal =
                                    selectedRatesTab && rfqData?.rfqType !== "contractor"
                                        ? (supplier?.vendorQuotations?.quotationSets?.slice(-1)[0]?.selectedTotalWithoutGst
                                            ?? supplier?.vendorQuotations?.quotationSets?.slice(-1)[0]?.totalWithoutGst
                                            ?? supplier?.vendorQuotations?.grandTotalWithoutGst
                                            ?? 0)
                                        : supplier?.vendorQuotations?.grandTotalWithoutGst || 0;

                                return (
                                    <button
                                        key={supplier?.supplierId}
                                        type="button"
                                        onClick={() => toggleVendorColumn(supplier?.supplierId)}
                                        className="flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left transition hover:bg-slate-50 focus:bg-slate-50 focus:outline-none"
                                    >
                                        <span className="min-w-0">
                                            <span className="block truncate text-[10px] font-semibold text-gray-700">
                                                {supplier?.vendorQuotations?.rank || "-"} · {supplier?.businessName || "Vendor"}
                                            </span>
                                            <span className="block text-[9px] text-gray-500">
                                                Rs. {new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(hiddenQuotedTotal)}
                                            </span>
                                        </span>
                                        <VisibilityIcon aria-hidden="true" style={{ fontSize: 16 }} />
                                    </button>
                                );
                            })}
                    </div>
                </div>
            )}

            {/* <button
                type="button"
                onClick={expandAllVendorColumns}
                disabled={collapsedVendorIndexes.length === 0}
                aria-label="Expand all vendors"
                title="Expand all vendors"
                        className="my-2 flex h-6 w-6 items-center justify-center border-[1px] border-[#767676] bg-white text-gray-600 transition hover:border-blue-500 hover:text-blue-600 disabled:cursor-not-allowed disabled:opacity-40"
            >
                        <ChevronRightIcon style={{ fontSize: 16 }} aria-hidden="true" />
            </button>

            <button
                type="button"
                onClick={collapseAllVendorColumns}
                disabled={sortedSuppliers.length === 0 || collapsedVendorIndexes.length === sortedSuppliers.length}
                aria-label="Collapse all vendors"
                title="Collapse all vendors"
                className="my-2 flex h-6 w-6 items-center justify-center border-[1px] border-[#767676] bg-white text-gray-600 transition hover:border-blue-500 hover:text-blue-600 disabled:cursor-not-allowed disabled:opacity-40"
            >
                <ChevronLeftIcon style={{ fontSize: 16 }} aria-hidden="true" />
            </button> */}
        </div>
    );

    const renderVisibilityMenuPortal = () => {
        if (!openVisibilityMenu || !visibilityMenuPosition || typeof document === "undefined") return null;

        const isRowsMenu = openVisibilityMenu === "rows";

        return createPortal(
            <div
                ref={visibilityMenuRef}
                className="fixed z-[9999] w-[250px] rounded-lg border border-gray-200 bg-white p-2 text-left shadow-xl"
                style={{
                    top: visibilityMenuPosition.top,
                    left: visibilityMenuPosition.left,
                }}
            >
                <div className="flex items-center justify-between gap-2 px-2 py-1">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                        {isRowsMenu ? "Show a line item" : "Show a vendor"}
                    </p>
                    <button
                        type="button"
                        onClick={() => {
                            if (isRowsMenu) showAllLineItems();
                            else expandAllVendorColumns();
                            setOpenVisibilityMenu(null);
                        }}
                        className="shrink-0 rounded border border-blue-200 bg-blue-50 px-2 py-0.5 text-[10px] font-semibold text-blue-700 transition hover:border-blue-300 hover:bg-blue-100 focus:outline-none focus:ring-1 focus:ring-blue-200"
                    >
                        Show all
                    </button>
                </div>

                <div className="max-h-56 overflow-y-auto">
                    {isRowsMenu
                        ? hiddenLineItems.map((item) => (
                            <button
                                key={item.key}
                                type="button"
                                onClick={() => {
                                    toggleLineItemVisibility(item.key);
                                    if (hiddenLineItems.length === 1) setOpenVisibilityMenu(null);
                                }}
                                className="flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left transition hover:bg-slate-50 focus:bg-slate-50 focus:outline-none"
                            >
                                <span className="min-w-0 truncate text-[10px] font-semibold text-gray-700">
                                    {item.serialNumber}. {item.label}
                                </span>
                                <VisibilityIcon className="shrink-0" aria-hidden="true" style={{ fontSize: 15 }} />
                            </button>
                        ))
                        : sortedSuppliers
                            .filter(supplier => collapsedVendorIds.has(String(supplier?.supplierId)))
                            .map(supplier => {
                                const hiddenQuotedTotal =
                                    selectedRatesTab && rfqData?.rfqType !== "contractor"
                                        ? (supplier?.vendorQuotations?.quotationSets?.slice(-1)[0]?.selectedTotalWithoutGst
                                            ?? supplier?.vendorQuotations?.quotationSets?.slice(-1)[0]?.totalWithoutGst
                                            ?? supplier?.vendorQuotations?.grandTotalWithoutGst
                                            ?? 0)
                                        : supplier?.vendorQuotations?.grandTotalWithoutGst || 0;

                                return (
                                    <button
                                        key={supplier?.supplierId}
                                        type="button"
                                        onClick={() => {
                                            toggleVendorColumn(supplier?.supplierId);
                                            if (collapsedVendorIndexes.length === 1) setOpenVisibilityMenu(null);
                                        }}
                                        className="flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left transition hover:bg-slate-50 focus:bg-slate-50 focus:outline-none"
                                    >
                                        <span className="min-w-0">
                                            <span className="block truncate text-[10px] font-semibold text-gray-700">
                                                {supplier?.vendorQuotations?.rank || "-"} · {supplier?.businessName || "Vendor"}
                                            </span>
                                            <span className="block text-[9px] text-gray-500">
                                                Rs. {new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(hiddenQuotedTotal)}
                                            </span>
                                        </span>
                                        <VisibilityIcon aria-hidden="true" style={{ fontSize: 16 }} />
                                    </button>
                                );
                            })}
                </div>
            </div>,
            document.body
        );
    };


    return (
        <>
            <style>
                {`
            .comparison-scrollbar::-webkit-scrollbar {
                height: 20px;
                width: 30px;
            }

            .comparison-scrollbar::-webkit-scrollbar-track {
                background: #f1f1f1;
            }

            .comparison-scrollbar::-webkit-scrollbar-thumb {
                background-color: #9ca3af;
                border-radius: 8px;
                border: 3px solid #f1f1f1;
            }

            .comparison-scrollbar::-webkit-scrollbar-thumb:hover {
                background-color: #6b7280;
            }

            .comparison-scrollbar {
                scrollbar-width: thick;
                scrollbar-color: #9ca3af #f1f1f1;
            }

            .comparison-table .fixed-column,
            .comparison-table thead th {
                z-index: 100 !important;
                position: sticky !important;
                top: 0 !important;
                background-color: #ffffff !important;
            }

            .comparison-table thead {
                position: sticky !important;
                top: 0 !important;
                z-index: 150 !important;
                isolation: isolate;
            }

            .comparison-table thead th {
                z-index: 160 !important;
            }

            .comparison-table thead > tr > th {
                vertical-align: top !important;
            }

            .comparison-table thead > tr > th.comparison-header-cell {
                height: var(--comparison-header-height, auto) !important;
                border-bottom: 2px solid #1E1E08 !important;
            }

            .comparison-table .comparison-fixed-header-content {
                min-height: 102px;
                height: calc(var(--comparison-header-height, 0px) - 20px);
            }

            .comparison-table .comparison-vendor-header-content {
                min-height: 102px;
                height: var(--comparison-header-height, auto);
            }

            .comparison-table thead .comparison-header-grid > * {
                white-space: nowrap;
            }

            .comparison-table thead th.fixed-column {
                z-index: 260 !important;
                box-shadow: 5px 0 8px -6px rgba(15, 23, 42, 0.45) !important;
                border-right: 1px solid #d1d5db !important;
            }

            .comparison-table thead > tr > th.comparison-vendor-header-cell {
                z-index: 30 !important;
                overflow: visible !important;
                box-shadow: 0 6px 10px -7px rgba(15, 23, 42, 0.62) !important;
            }

            .comparison-table tbody .sticky.left-0 {
                z-index: 40 !important;
                width: var(--comparison-fixed-width) !important;
                min-width: var(--comparison-fixed-width) !important;
                max-width: var(--comparison-fixed-width) !important;
                box-sizing: border-box !important;
            }

            .comparison-table tbody div.sticky.left-0 {
                box-shadow: 6px 0 10px -5px rgba(15, 23, 42, 0.48) !important;
            }

            .comparison-table tbody td.sticky.left-0 {
                box-shadow: 6px 0 10px -5px rgba(15, 23, 42, 0.48) !important;
                border-right: 1px solid #d1d5db !important;
            }

            .comparison-table tbody td.comparison-sticky-rfq-cell {
                position: sticky !important;
                left: 0 !important;
                z-index: 120 !important;
                overflow: visible !important;
                border-right: 1px solid #bfc7d1 !important;
                box-shadow: 7px 0 11px -6px rgba(15, 23, 42, 0.58) !important;
                isolation: isolate;
            }

            .comparison-table tbody td.comparison-sticky-rfq-cell::after {
                content: "";
                position: absolute;
                top: -1px;
                right: -8px;
                bottom: -1px;
                width: 8px;
                pointer-events: none;
                background: linear-gradient(90deg, rgba(15, 23, 42, 0.16), rgba(15, 23, 42, 0));
            }

            .comparison-table tbody div.sticky.left-0 > td.sticky.left-0 {
                box-shadow: none !important;
            }

            ${collapsedVendorColumnStyles}
            `}
            </style>

            <div className="relative overflow-x-auto comparison-scrollbar" style={{ fontFamily: "goldman_sans", maxHeight: "calc(100vh - 12px)" }}>

                {/* <button
                type="button"
                onClick={() => handleDownloadComparisonExcel(rfqData, sortedSuppliers, setExcelLoading, setLoadingMessage, enqueueSnackbar)}
                className="bg-blue-500 text-white px-3 py-1 text-xs"
            >
                Download Excel
            </button> */}

                <table
                    ref={comparisonTableRef}
                    className={`comparison-table h-full ${collapsedVendorTableClasses}`}
                    style={{
                        "--comparison-fixed-width": `${rfqData?.rfqType === "contractor" ? 560 : 480}px`,
                        "--comparison-header-height": (
                            sortedSuppliers.length > 0 &&
                            collapsedVendorIds.size < sortedSuppliers.length &&
                            comparisonHeaderHeight
                        ) ? `${comparisonHeaderHeight}px` : undefined,
                        visibility: (
                            sortedSuppliers.length === 0 ||
                            collapsedVendorIds.size >= sortedSuppliers.length ||
                            comparisonHeaderHeight
                        ) ? "visible" : "hidden",
                    }}
                >
                    {/* header */}
                    <thead className="bg-white">
                        <tr>
                            {/* fixed header */}
                            <th
                                className={`comparison-header-cell fixed-column z-10 border-r sticky top-0 left-0 bg-white text-left overflow-visible ${rfqData?.rfqType === "contractor" ? "min-w-[560px] max-w-[560px]" : "min-w-[480px] max-w-[480px]"}`}
                                style={{
                                    minWidth: rfqData?.rfqType === "contractor" ? 560 : 480,
                                    maxWidth: rfqData?.rfqType === "contractor" ? 560 : 480,
                                    width: rfqData?.rfqType === "contractor" ? 560 : 480,
                                    boxShadow: '4px 0 4px rgba(0, 0, 0, 0.1)'
                                }}
                            >
                                <div className='h-[20px] bg-[#343417]' style={{ boxShadow: '4px 0 4px rgba(0, 0, 0, 0.1)', }}></div>
                                <div className='comparison-fixed-header-content px-[22px] border-r border-r-gray-300 flex flex-col justify-start overflow-visible' style={{ width: rfqData?.rfqType === "contractor" ? 560 : 480, maxWidth: rfqData?.rfqType === "contractor" ? 560 : 480, boxSizing: 'border-box', boxShadow: '4px 0 4px rgba(0, 0, 0, 0.1)', }}>
                                    <div className="comparison-header-details flex min-h-0 flex-1 flex-col">
                                    <div className='pt-2'>
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="min-w-0">
                                            <h3 className="text-sm">Vendor Comparison</h3>
                                            <p className="pt-2 text-xs font-normal text-[#6B7280]">Note: All values are in Rs.</p>
                                            </div>
                                            <div className="flex shrink-0 items-center gap-1">
                                                {renderHiddenLineItemControls()}
                                                {renderVendorVisibilityControls()}
                                            </div>
                                        </div>
                                    </div>
                                    {/* <div className="flex justify-start items-center pb-2 space-x-[22px]">
                                        <div className="flex items-center">
                                            <input
                                                id="with-gst"
                                                type="radio"
                                                value="with-gst"
                                                name="gst-radio"
                                                checked={withGst}
                                                onChange={handleGstChange}
                                                className="w-3 h-3 mb-1 bg-gray-100  checked:bg-black checked:border-black focus:ring-black focus:ring-1"
                                            />
                                            <label
                                                htmlFor="with-gst"
                                                className="ml-2 text-xs font-medium text-gray-900 dark:text-gray-300"
                                            >
                                                with GST
                                            </label>
                                        </div>
                                        <div className="flex items-center">
                                            <input
                                                id="without-gst"
                                                type="radio"
                                                value="without-gst"
                                                name="gst-radio"
                                                checked={!withGst}
                                                onChange={handleGstChange}
                                                className="w-3 h-3  mb-1 border-gray-300 checked:bg-black checked:border-black focus:ring-black focus:ring-1"
                                            />
                                            <label
                                                htmlFor="without-gst"
                                                className="ml-2 text-xs font-medium text-gray-900 dark:text-gray-300"
                                            >
                                                without GST
                                            </label>
                                        </div>
                                    </div> */}
                                    {/* Estimated Cost: 4,90,000 {withGst ? 'w/ GST' : 'w/o GST'} */}
                                    {/* {!(closeRfqForm || selectedRatesTab) && <h3 className="text-xs pt-1 pb-1 font-normal text-[#44441A]">
                                        Estimated Delivery: <span className=' text-xs'> {rfqData?.providingDate ? new Date(rfqData.providingDate).toLocaleDateString('en-GB') : ''}</span>
                                    </h3>} */}

                                    {/* {!(closeRfqForm || selectedRatesTab) && (
                                        <h3 className="pb-4 text-xs font-normal text-[#44441A]">
                                            RFQ Budget: Rs.<span> {new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(rfqData?.rfqBudget)}</span>
                                        </h3>
                                    )} */}

                                    {(closeRfqForm || selectedRatesTab) && (
                                        <div
                                            className={`${(rfqData?.rfqType === "material" || rfqData?.rfqType === "labour") && ""} h-full sticky left-0 text-xs font-normal bg-white w-full min-w-0 overflow-hidden`}
                                        >

                                                {/* New Line for Total Selected Quotation Value in close rfq form and selected rates */}
                                                {/* {rfqData?.rfqBudget > 0 && (() => {
                                                    const budget = rfqData?.rfqBudget;
                                                    const quotation = totalSelectedQuotationValue || 0;
                                                    const diff = budget - quotation;
                                                    const isOverBudget = diff < 0;
                                                    const formattedDiff = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(Math.abs(diff));

                                                    return (
                                                        <p className="text-xs flex justify-between">
                                                            <span>Budget Deviation:{" "}</span>
                                                            <span className={`${isOverBudget ? 'text-red-600' : 'text-green-600'}`}>
                                                                Rs. {formattedDiff} {isOverBudget ? '▲' : '▼'}
                                                            </span>
                                                        </p>
                                                    );
                                                })()} */}


                                                {/* New Line for Total Selected Quotation Value */}
                                                <p className='flex justify-between w-full'>
                                                    <span className="min-w-0">Total Selected Quotation Value:</span>
                                                    <span className="shrink-0 pl-2">
                                                        Rs. {new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(totalSelectedQuotationValue.toFixed(1))}
                                                    </span>
                                                </p>

                                                <p className='flex justify-between w-full pt-[0px]'>
                                                    <span className="min-w-0">Difference From L1 & Selected:</span>
                                                    <span className={`${differencefromL1ToSelected < 0 ? 'text-red-500' : 'text-green-500'} shrink-0 pl-2`}>
                                                        Rs. {new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(differencefromL1ToSelected.toFixed(1))}
                                                    </span>
                                                </p>

                                                <p className='flex justify-between w-full pb-1'>
                                                    <span className="min-w-0">Lowest Total:</span>
                                                    <span className="shrink-0 pl-2">
                                                        Rs. {new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format((rfqData?.rfqType === 'material' ? totalLowestPriceSum : 0) +
                                                            (rfqData?.rfqType === 'labour' ? totalLowestLabourPriceSum : 0) +
                                                            otherstotalLowestPriceSum
                                                        )}
                                                    </span>
                                                </p>
                                        </div>
                                    )}

                                    </div>

                                    <div className={`comparison-header-grid grid ${rfqData?.rfqType === "contractor" ? "grid-cols-20" : "grid-cols-18"} pb-[4px] mt-auto items-end w-full min-w-0`}>
                                        {(() => {
                                            const isLastRateVisible =
                                                rfqData?.rfqType === "material" &&
                                                !selectedRatesTab &&
                                                !closeRfqForm;
                                            const itemSpan =
                                                rfqData?.rfqType === "labour"
                                                    ? 5 + (!showBudgetRateColumn ? 3 : 0) + (!showBudgetAmountColumn ? 4 : 0)
                                                    : rfqData?.rfqType === "contractor"
                                                        ? 8 + (!showBudgetAmountColumn ? 4 : 0)
                                                        : (isLastRateVisible ? 3 : 3) +
                                                            (!showBudgetRateColumn ? (isLastRateVisible ? 3 : 4) : 0) +
                                                            (!showBudgetAmountColumn ? (isLastRateVisible ? 3 : 4) : 0);
                                            const materialMetricSpan = isLastRateVisible ? "col-span-3" : "col-span-4";

                                            return (
                                                <>
                                                    {/* Line-item visibility */}
                                                    {["contractor", "labour", "material"].includes(rfqData?.rfqType) && (
                                                        <div className="col-span-1 flex items-center justify-center text-xs">
                                                            <button
                                                                type="button"
                                                                onClick={toggleAllLineItems}
                                                                disabled={comparisonLineItems.length === 0}
                                                                aria-label={areAllLineItemsHidden ? "Show all line items" : "Hide all line items"}
                                                                title={areAllLineItemsHidden ? "Show all line items" : "Hide all line items"}
                                                                className="flex h-5 w-5 shrink-0 items-center justify-center rounded bg-transparent text-slate-700 transition hover:bg-blue-50 hover:text-blue-700 focus:outline-none focus:ring-1 focus:ring-blue-200 disabled:cursor-default disabled:opacity-35"
                                                            >
                                                                {areAllLineItemsHidden ? (
                                                                    <VisibilityIcon aria-hidden="true" style={{ fontSize: 16 }} />
                                                                ) : (
                                                                    <VisibilityOffIcon aria-hidden="true" style={{ fontSize: 16 }} />
                                                                )}
                                                            </button>
                                                        </div>
                                                    )}

                                                    {/* Items */}
                                                    {["contractor", "labour", "material"].includes(rfqData?.rfqType) && (
                                                        <p
                                                            className="text-xs"
                                                            style={{ gridColumn: `span ${itemSpan} / span ${itemSpan}` }}
                                                        >
                                                            Items
                                                        </p>
                                                    )}

                                                    {/* Quantity */}
                                                    {(rfqData?.rfqType === "material" ||
                                                        rfqData?.rfqType === "labour") && (
                                                            <h3
                                                                className={`text-xs ${rfqData?.rfqType === "labour"
                                                                    ? "col-span-2" // 🔒 DO NOT CHANGE (labour working)
                                                                    : "col-span-2"
                                                                    }`}
                                                            >
                                                                Qty
                                                            </h3>
                                                    )}

                                                    {rfqData?.rfqType === "contractor" && (
                                                        <h3 className="text-xs col-span-3 text-center">
                                                            Qty
                                                        </h3>
                                                    )}

                                                    {/* Last Rate (ONLY for material) */}
                                                    {rfqData?.rfqType === "material" && isLastRateVisible && (
                                                        <span className="text-xs text-right col-span-3">
                                                            Last Rate
                                                        </span>
                                                    )}

                                                    {/* Budget */}
                                                    {rfqData?.rfqType === "labour" && (
                                                        showBudgetRateColumn && <h3 className="text-xs text-right pl-1 col-span-3">
                                                            <span className="whitespace-nowrap">Budget rate</span>
                                                            {/* <br />
                                                            <span className="whitespace-nowrap">(Rs.)</span> */}
                                                        </h3>
                                                    )}

                                                    {rfqData?.rfqType === "material" && showBudgetRateColumn && (
                                                        <h3
                                                            className={`text-xs text-right pl-1 ${materialMetricSpan}`}
                                                        > 
                                                            Budget rate
                                                        </h3>
                                                    )}

                                                    {/* Budget Amt */}
                                                    {rfqData?.rfqType === "labour" && (
                                                        showBudgetAmountColumn && <h3 className="text-xs text-right pl-1 col-span-4">
                                                            Budget Amt
                                                        </h3>
                                                    )}

                                                    {rfqData?.rfqType === "contractor" && showBudgetAmountColumn && (
                                                        <h3 className="text-xs text-right pl-1 col-span-4">
                                                            Budget Amt
                                                        </h3>
                                                    )}

                                                    {rfqData?.rfqType === "material" && showBudgetAmountColumn && (
                                                        <h3
                                                            className={`text-xs text-right pl-1 ${materialMetricSpan}`}
                                                        >
                                                            Budget Amt
                                                        </h3>
                                                    )}

                                                    {/* Lowest Rate */}
                                                    {rfqData?.rfqType === "labour" && (
                                                        <span className="text-xs text-right col-span-3">
                                                            Lowest Rate
                                                        </span>
                                                    )}

                                                    {rfqData?.rfqType === "contractor" && (
                                                        <span className="text-xs text-right col-span-4">
                                                            Lowest Rate
                                                        </span>
                                                    )}

                                                    {rfqData?.rfqType === "material" && (
                                                        <span
                                                            className={`text-xs text-right ${materialMetricSpan}`}
                                                        >
                                                            Lowest Rate
                                                        </span>
                                                    )}


                                                </>
                                            );
                                        })()}
                                    </div>

                                </div>
                            </th>
                            {/* quotation header */}
                            {sortedSuppliers?.map((supplier, index) => {

                                const supplierMaterial = supplier?.vendorQuotations?.quotationSets?.slice(-1)[0]?.materialDetails || [];
                                const selectedMaterials = effectiveSelectedMaterialsMap?.[supplier?.supplierId] || [];

                                // Sum up selectedSupplierPrice + selectedTransportCost for selected materials
                                const selectedMaterialBasePriceSum = selectedMaterials.reduce((sum, material) => {
                                    return sum + (parseFloat(material.selectedSupplierPrice) || 0);
                                }, 0);

                                const supplierCharges = chargeInputs[supplier.supplierId] || {};

                                // Sum up all charges except "transport" if isAdvancedTransport is true
                                const totalExtraCharges = Object.entries(supplierCharges)
                                    .filter(([chargeType]) => !isAdvancedTransport || chargeType.toLowerCase() !== "transport")
                                    .reduce((sum, [, chargeValue]) => sum + (parseFloat(chargeValue) || 0), 0);

                                // Only add extra charges if at least one material is selected
                                const finalSelectedTotal = selectedMaterials.length > 0
                                    ? selectedMaterialBasePriceSum + (
                                        rfqData?.rfqType === "contractor"
                                            ? getContractorVendorExtraCharges(supplier)
                                            : totalExtraCharges
                                    )
                                    : selectedMaterialBasePriceSum;


                                const isL1 = index === 0;
                                const supplierId = String(supplier?.supplierId);
                                const isVendorCollapsed = collapsedVendorIds.has(supplierId);
                                const vendorAccent = ["#D97706", "#65A30D", "#6366F1", "#0EA5E9"][index % 4];

                const isChecked = rfqData?.rfqType === "contractor"
                                    ? isAllVendorContractorScopesSelected(supplier.supplierId)
                                    : selectedVendors?.includes(supplier.supplierId); // Check if this supplier is selected
                                const supplierTotal = supplier.vendorQuotations?.grandTotalWithoutGst || 0


                                const quotedTotal =
                                    selectedRatesTab && rfqData?.rfqType !== "contractor"
                                        ? (supplier?.vendorQuotations?.quotationSets?.slice(-1)[0]?.selectedTotalWithoutGst
                                            ?? supplier?.vendorQuotations?.quotationSets?.slice(-1)[0]?.totalWithoutGst
                                            ?? supplierTotal)
                                        : supplierTotal;
                const selectedTotal = showSelectedValueColumn
                                    ? (rfqData?.rfqType === "contractor"
                                        ? finalSelectedTotal
                                        : finalSelectedTotal)
                                    : quotedTotal;
                                const renderMaterialHeader = () => (
                                    <div className="comparison-header-grid grid grid-cols-18 w-full gap-x-1 mt-auto">
                                        <div className={`${canSelectRates ? "col-span-4" : (showSelectedValueColumn ? "col-span-4" : "col-span-5")} flex pr-2`}>
                                            {canSelectRates && (
                                                <input
                                                    type="checkbox"
                                                    checked={isAllVendorMaterialsSelected(supplier.supplierId)}
                                                    onChange={() => handleVendorCheckboxChange(supplier.supplierId)}
                                                    disabled={isEditRatesLocked}
                                                    className="h-3 w-3 form-checkbox text-blue-600 bg-gray-100 mr-1"
                                                />
                                            )}
                                            <h3 className="text-xs">Current Rate</h3>
                                        </div>

                                        {canSelectRates && (
                                            <span className="text-xs col-span-2 text-right px-1">Qty</span>
                                        )}

                                        <h3 className={`text-xs text-right ${canSelectRates ? "col-span-2" : (showSelectedValueColumn ? "col-span-3" : "col-span-4")}`}>First Rate</h3>

                                        <span className={`text-xs text-right ${canSelectRates ? "col-span-3" : (showSelectedValueColumn ? "col-span-3" : "col-span-4")}`}>Difference</span>

                                        {canSelectRates && isAdvancedTransport ? (
                                            <>
                                                <span className="text-xs col-span-2 text-right">Transport</span>
                                                <span className={`text-xs text-right pl-2 whitespace-nowrap ${showSelectedValueColumn ? "col-span-3" : "col-span-5"}`}>Quoted Value</span>
                                                {showSelectedValueColumn && (
                                                    <span className="text-xs text-right whitespace-nowrap col-span-2">Selected</span>
                                                )}
                                            </>
                                        ) : (
                                            <>
                                                <span className={`text-xs text-right pl-2 ${canSelectRates ? (showSelectedValueColumn ? "col-span-4" : "col-span-7") : (showSelectedValueColumn ? "col-span-4" : "col-span-5")}`}>Quoted Value</span>
                                                {showSelectedValueColumn && (
                                                    <span className={`text-xs text-right ${canSelectRates ? "col-span-3" : "col-span-4"}`}>Selected Value</span>
                                                )}
                                            </>
                                        )}
                                    </div>
                                );

                                const renderLabourHeader = () => (
                                    <div className={`comparison-header-grid grid ${
                                        canSelectRates
                                            ? (showSelectedValueColumn ? "grid-cols-6" : "grid-cols-5")
                                            : (showSelectedValueColumn ? "grid-cols-5" : "grid-cols-4")
                                    } w-full gap-x-2 items-center text-right  mt-auto`}>
                                        <h3 className="text-xs text-left flex items-center">
                                            {canSelectRates && (
                                                <input
                                                    type="checkbox"
                                                    checked={isAllVendorLaboursSelected(supplier.supplierId)}
                                                    onChange={() => handleVendorLabourCheckboxChange(supplier.supplierId)}
                                                    disabled={isEditRatesLocked}
                                                    className="h-3 w-3 form-checkbox text-blue-600 bg-gray-100 mr-1 focus:ring-0"
                                                />
                                            )}
                                            Current Rate
                                        </h3>

                                        {(canSelectRates || selectedRatesTab) && <h3 className="text-xs">Qty</h3>}

                                        <h3 className="text-xs">First Rate</h3>

                                        <span className="text-xs">Difference</span>

                                        <span className="text-xs">Quoted Value</span>

                                        {showSelectedValueColumn && <span className="text-xs">Selected Value</span>}
                                    </div>
                                );

                                const renderContractorHeader = () => (
                                    <div className={`comparison-header-grid grid ${
                                        canSelectRates
                                            ? (showSelectedValueColumn
                                                ? "grid-cols-[1.25fr_0.75fr_1fr_1fr_1fr]"
                                                : "grid-cols-[1.25fr_0.75fr_1fr_1fr]")
                                            : "grid-cols-3"
                                    } text-right w-full gap-x-3`}>

                                        <h3
                                            className={`w-full min-w-0 text-xs flex items-center ${canSelectRates ? "text-left" : "justify-end text-right"}`}
                                            style={{ textAlign: canSelectRates ? "left" : "right" }}
                                        >
                                             {canSelectRates && (
                                                <input
                                                    type="checkbox"
                                                    checked={isChecked}
                                                    onChange={() => handleContractorVendorCheckboxChange(supplier.supplierId)}
                                                    disabled={isEditRatesLocked}
                                                    className='mr-1'
                                                />
                                            )}
                                            {canSelectRates ? "Current Quote" : "First Quote"}
                                            </h3>

                                        {canSelectRates && <h3 className="text-xs text-center whitespace-nowrap">Qty</h3>}

                                        <h3 className="text-xs whitespace-nowrap">{canSelectRates ? "First Quote" : "Current Quote"}</h3>
                                       
                                        <span className="text-xs whitespace-nowrap">Difference</span>
                                    
                                        {showSelectedValueColumn && <h3 className="text-xs whitespace-nowrap">Selected Value</h3>}

                                    </div>
                                );

                                return (
                                    <th
                                        key={index}
                                        className={`comparison-header-cell comparison-vendor-header-cell ${
                                            rfqData?.rfqType === 'labour'
                                                ? 'min-w-[600px]'
                                                : rfqData?.rfqType === 'contractor'
                                                    ? 'min-w-[680px]'
                                                    : 'min-w-[540px]'
                                        } z-0 sticky top-0 ${isVendorCollapsed ? "px-1 pt-0" : "px-4 pt-0"} bg-white border-gray-300 border-r`}
                                    >
                                        {isVendorCollapsed ? (
                                            <div
                                                className="flex min-h-[102px] h-full flex-col items-center justify-between gap-2 rounded-md border border-slate-200 bg-slate-50 px-1.5 py-2"
                                                style={{ borderTop: `4px solid ${vendorAccent}` }}
                                            >
                                                <button
                                                    type="button"
                                                    onClick={() => toggleVendorColumn(supplierId)}
                                                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-600 shadow-sm transition hover:border-blue-500 hover:text-blue-600"
                                                    aria-label={`Show ${supplier.businessName} column`}
                                                    title={`Show ${supplier.businessName}`}
                                                >
                                                    <VisibilityIcon aria-hidden="true" style={{ fontSize: 20 }} />
                                                </button>
                                                <span className="rounded-full bg-white px-1.5 py-1 text-[10px] font-bold text-[#4B4B1C]">
                                                    {supplier.vendorQuotations?.rank || "-"}
                                                </span>
                                                <span className="max-h-[86px] overflow-hidden text-center text-[10px] font-bold leading-tight text-[#4B4B1C] [writing-mode:vertical-rl] rotate-180">
                                                    Rs. {new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(quotedTotal)}
                                                </span>
                                            </div>
                                        ) : (
                                        <div className='comparison-vendor-header-content sticky top-0 bg-white flex flex-col pt-3'>
                                            <div className="comparison-header-details flex min-h-0 flex-1 flex-col">
                                            <div className='flex justify-between'>
                                                <div className='flex min-w-0 items-start justify-start gap-3'>
                                                   <div className="relative w-[36px] h-[36px] rounded-full bg-[#343417] flex items-center justify-center">
                                                   
                                                    <p className="text-sm font-normal text-white pt-1">
                                                        {getInitials(supplier.businessName)}
                                                    </p>
                                                    </div>
                                                    <div className='flex flex-col justify-between whitespace-nowrap'>
                                                    <h3 className="flex items-center gap-1.5 text-sm text-left text-[#4B4B1C]">
                                                        {supplier.canViewRank && (
                                                            <span className="group relative flex h-2.5 w-2.5 shrink-0">
                                                                {/* Ping */}
                                                                <span className="absolute inset-0 rounded-full bg-orange-400 opacity-75 animate-ping" />

                                                                {/* Center dot */}
                                                                <span className="relative h-2.5 w-2.5 rounded-full bg-orange-500 border border-white cursor-pointer" />

                                                                {/* Tooltip */}
                                                                <span className="absolute left-4 top-1/2 z-50 hidden -translate-y-1/2 whitespace-nowrap rounded-md bg-gray-800 px-2 py-1 text-xs font-medium text-white shadow-lg group-hover:block">
                                                                    This vendor can see their rank
                                                                </span>
                                                            </span>
                                                        )}

                                                        <span>
                                                            {supplier.businessName} ({supplier.vendorQuotations?.rank})
                                                        </span>
                                                        <button
                                                            type="button"
                                                            onClick={() => toggleVendorColumn(supplierId)}
                                                            className="flex align-top h-5 w-5 shrink-0 cursor-pointer items-center justify-center rounded bg-transparent text-slate-700 transition hover:bg-blue-50 hover:text-blue-700 focus:outline-none focus:ring-1 focus:ring-blue-200"
                                                            aria-label={`Hide ${supplier.businessName} quotation`}
                                                            title={`Hide ${supplier.businessName} quotation`}
                                                        >
                                                            <VisibilityOffIcon style={{ fontSize: 18 }} aria-hidden="true" />
                                                        </button>
                                                    </h3>
                                                    <div className="flex items-center gap-2 text-xs text-left font-medium text-[#969688]">
                                                            <span className="flex items-center gap-1 text-xs text-[#969688]">
                                                                <svg
                                                                    className="w-3 h-3 shrink-0"
                                                                    fill="currentColor"
                                                                    viewBox="0 0 24 24"
                                                                >
                                                                    <path d="M6.62 10.79a15.05 15.05 0 006.59 6.59l2.2-2.2a1 1 0 011.01-.24 11.36 11.36 0 003.56.57 1 1 0 011 1v3.54a1 1 0 01-1 1A17 17 0 013 5a1 1 0 011-1h3.54a1 1 0 011 1 11.36 11.36 0 00.57 3.56 1 0 01-.24 1.01l-2.25 2.22z" />
                                                                </svg>

                                                                <span>+91-{supplier?.contactDetails?.[0]?.mobile}</span>
                                                            </span>

                                                            <span>|</span>

                                                            <span>
                                                                {supplier?.vendorSource
                                                                    ? supplier.vendorSource === "MARKETPLACE"
                                                                        ? "Marketplace"
                                                                        : supplier.vendorSource === "INTERNAL"
                                                                            ? "Internal"
                                                                            : supplier.vendorSource
                                                                    : "N/A"}
                                                            </span>

                                                            {rfqData?.status === "CLOSED" && supplier?.isAwarded && (
                                                                <>
                                                                    <span>|</span>
                                                                    <BookmarkIcon
                                                                        titleAccess="Awarded vendor"
                                                                        style={{ fontSize: 18, color: '#28A745' }}
                                                                    />
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex items-start gap-2">
                                                    <QuotationUpdateRequestHistory supplier={supplier} />
                                                    <MoreOptions index={index} rfqData={{ ...rfqData, suppliers: [supplier] }} allSuppliers={rfqData?.suppliers?.filter((supplier) => supplier?.quotationReceived && !isSupplierExcludedFromComparison(supplier))} userProfile={userProfile} openPdfForSupplier={openPdfForSupplier} onExcludeFromComparison={handleExcludeFromComparison} onRequestQuotationUpdate={handleRequestQuotationUpdate}/>
                                                </div>
                                            </div>

<div className=' pt-2'>
                                            {(closeRfqForm || selectedRatesTab) && (
                                            <h3
                                                className={`text-xs text-left font-normal text-[#44441A] ${
                                                rfqData?.rfqBudget > 0 ? "" : ""
                                                }`}
                                            >
                                                Selected Total: Rs.{" "}
                                                {new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(
                                                selectedTotal
                                                )}
                                            </h3>
                                            )}

                                            <div className=''>
                                                 {rfqData?.rfqBudget > 0 && !closeRfqForm && !selectedRatesTab && (() => {
                                                    const budget = rfqData?.rfqBudget;
                                                    const quotation = supplier?.vendorQuotations?.grandTotalWithoutGst || 0;
                                                    const diff = budget - quotation;
                                                    const isOverBudget = diff < 0;
                                                    const formattedDiff = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(Math.abs(diff));
                                                    const deviationPercentage = budget > 0
                                                        ? Math.abs((diff / budget) * 100).toFixed(2)
                                                        : '0.00';

                                                    return (
                                                        <h3 className="text-xs font-normal text-left" style={{ marginTop: '2px' }}>
                                                            <span className="">Budget Deviation :</span>{" "}
                                                            <span className={isOverBudget ? "text-red-600" : "text-green-600"}>
                                                                Rs. {formattedDiff} ({deviationPercentage}%) {isOverBudget ? <span style={{ color: 'red' }}>▲</span> : <span style={{ color: 'green' }}>▼</span>}
                                                            </span>
                                                        </h3>
                                                    );
                                                })()}
                                                <div className={`flex text-xs font-normal space-x-2 pb-1 ${(closeRfqForm || selectedRatesTab) ? "pt-1 pb-2" : "pb-2"}`}>
                                                    <h3 className={`text-left`}>
                                                        {(closeRfqForm || selectedRatesTab) ? "Original Total:" : "Original Total:"} Rs. {new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(
                                                            withGst ? supplier?.vendorQuotations?.grandTotalWithGst : supplier?.vendorQuotations?.grandTotalWithoutGst
                                                        )}
                                                    </h3>
                                                </div>
                                            </div>
</div>
                                            </div>
                                           <div className="mt-auto">
                                            {
                                                rfqData?.rfqType === "material"
                                                ? renderMaterialHeader()
                                                : rfqData?.rfqType === "labour"
                                                ? renderLabourHeader()
                                                : renderContractorHeader()
                                            }
                                            </div>



                                            {/* {(closeRfqForm || selectedRatesTab) && <div className="flex justify-between pb-[6px]">
                                            <h3 className="text-xs">Item Rate</h3>
                                            {isAdvancedTransport && <h3 className="text-xs">Transport Rate</h3>}
                                            <h3 className="text-xs">Total</h3>
                                        </div>} */}
                                        </div>
                                        )}
                                    </th>)
                            })}
                        </tr>
                    </thead>
                    {/* <CloseRfqOpt/> */}

                    <tbody className='overflow-y-auto  bg-white mb-8 h-[520px]'>
                        {rfqData?.rfqType === 'material' && (
                            closeRfqForm || selectedRatesTab ? (
                                <SelectionMaterialRFQTable
                                    rfqData={rfqData}
                                    sortedSuppliers={sortedSuppliers}
                                    withGst={withGst}
                                    selectedCheckboxes={selectedCheckboxes}
                                    setSelectedCheckboxes={setSelectedCheckboxes}
                                    // openDropdown={openDropdown}
                                    handleMaterialDropdown={handleMaterialDropdown}
                                    onTotalLowestPriceSum={handleTotalLowestPriceSum}
                                    closeRfqForm={closeRfqForm}
                                    isAdvancedTransport={isAdvancedTransport}
                                    selectedMaterialsMap={effectiveSelectedMaterialsMap}
                                    setSelectedMaterialsMap={setSelectedMaterialsMap}
                                    selectedRatesTab={selectedRatesTab}
                                    isEditRatesLocked={isEditRatesLocked}
                                    ALLOW_MULTI_VENDOR_PER_MATERIAL={ALLOW_MULTI_VENDOR_PER_MATERIAL}
                                    showSelectedValueColumn={showSelectedValueColumn}
                                    hasBudgetColumns={hasMaterialBudgetColumns}
                                    hiddenLineItemKeys={hiddenLineItemKeys}
                                    getLineItemKey={getComparisonLineItemKey}
                                    renderLineItemHideButton={renderLineItemHideButton}
                                />
                            ) : (
                                <MaterialRFQTable
                                    rfqData={rfqData}
                                    sortedSuppliers={sortedSuppliers}
                                    withGst={withGst}
                                    openDropdown={openDropdown}
                                    handleMaterialDropdown={handleMaterialDropdown}
                                    onTotalLowestPriceSum={handleTotalLowestPriceSum}
                                    closeRfqForm={closeRfqForm}
                                    isAdvancedTransport={isAdvancedTransport}
                                    goToRatesTab={() =>
                                    setTab(rfqData?.status === "CLOSED" ? "Approved Rates" : "Selected Rates")
                                    }                                    
                                    selectedMaterialsMap={effectiveSelectedMaterialsMap}
                                    hasBudgetColumns={hasMaterialBudgetColumns}
                                    hiddenLineItemKeys={hiddenLineItemKeys}
                                    getLineItemKey={getComparisonLineItemKey}
                                    renderLineItemHideButton={renderLineItemHideButton}
                                />
                            )
                        )}

                        {rfqData?.rfqType === 'labour' && (
                            <LabourRFQTable
                                rfqData={rfqData}
                                sortedSuppliers={sortedSuppliers}
                                withGst={withGst}
                                userProfile={userProfile}
                                closeRfqForm={closeRfqForm}
                                openDropdown={openDropdown}
                                handleMaterialDropdown={handleMaterialDropdown}
                                onTotalLowestLabourCostChange={handleTotalLowesLabourtPriceSum}
                                selectedRatesTab={selectedRatesTab}
                                isEditRatesLocked={isEditRatesLocked}
                                selectedMaterialsMap={effectiveSelectedMaterialsMap}
                                setSelectedMaterialsMap={setSelectedMaterialsMap}
                                showSelectedValueColumn={showSelectedValueColumn}
                                canSelectRates={canSelectRates}
                                hasBudgetColumns={hasLabourBudgetColumns}
                                hiddenLineItemKeys={hiddenLineItemKeys}
                                getLineItemKey={getComparisonLineItemKey}
                                renderLineItemHideButton={renderLineItemHideButton}
                            />
                        )}
                        {rfqData?.rfqType === 'contractor' && (
                            <ContractorRFQTable
                                rfqData={rfqData}
                                sortedSuppliers={sortedSuppliers}
                                withGst={withGst}
                                openDropdown={openDropdown}
                                handleMaterialDropdown={handleMaterialDropdown}
                                handleSectionClick={handleSectionClick}
                                selectedVendors={selectedVendors}
                                selectedMaterialsMap={effectiveSelectedMaterialsMap}
                                setSelectedMaterialsMap={setSelectedMaterialsMap}
                                setSelectedVendors={setSelectedVendors}
                                selectedRatesTab={selectedRatesTab}
                                isEditRatesLocked={isEditRatesLocked}
                                showSelectedValueColumn={showSelectedValueColumn}
                                canSelectRates={canSelectRates}
                                hasBudgetAmountColumn={hasContractorBudgetAmountColumn}
                                hiddenLineItemKeys={hiddenLineItemKeys}
                                getLineItemKey={getComparisonLineItemKey}
                                renderLineItemHideButton={renderLineItemHideButton}
                            />
                        )}


                        {/* Sub total Row */}
                        <tr>
                            {/* fixed sub total */}
                            <div className='h-full sticky left-0 z-[120] bg-white' style={{ boxShadow: '4px 0 4px rgba(0, 0, 0, 0.1)', }}>
                                <td
                                    className={`comparison-sticky-rfq-cell sticky left-0 align-top px-4 bg-white ${rfqData?.rfqType === "contractor" ? "min-w-[560px]" : "min-w-[480px]"}`}
                                    style={{ boxShadow: '4px 0 4px rgba(0, 0, 0, 0.1)' }}
                                >
                                    <p className='border-b w-full text-[#004491] py-1 bg-[#E7EAEF] flex justify-between items-center  text-xs px-2  '>
                                        <span>
                                            {rfqData?.rfqType === 'material' ? `Material Amount:` : rfqData?.rfqType === 'labour' ? 'Total Basic Amount' : rfqData?.rfqType === 'contractor' ? 'Sub Total' : null}
                                        </span>
                                        <span>
                                            {/* {rfqData?.rfqType === 'material' || rfqData?.rfqType === 'labour' ? ( */}
                                            <div className='flex items-center text-xs text-[#004491]'>
                                                <p>
                                                    Rs. {
                                                        new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(
                                                            rfqData?.rfqType === 'material'
                                                                ? totalLowestPriceSum
                                                                : rfqData?.rfqType === 'labour'
                                                                    ? totalLowestLabourPriceSum
                                                                    : rfqData?.rfqType === 'contractor'
                                                                        ? totalLowestContractorPriceSum
                                                                        : 0
                                                        )
                                                    }
                                                </p>
                                            </div>
                                            {/* ) : null} */}
                                        </span>
                                    </p>
                                </td>
                            </div>

                            {/* sub total in quotation */}
                            {sortedSuppliers?.map((supplier, index) => {
                                const vendorQuotation = supplier?.vendorQuotations?.quotationSets?.slice(-1)?.[0];
                                const selectedMaterialAmount = getMaterialVendorSelectedAmount(supplier?.supplierId);
                                const selectedLabourAmount = getLabourVendorSelectedAmount(supplier?.supplierId);
                                const selectedContractorAmount = getContractorVendorSelectedAmount(supplier?.supplierId);
                                return (
                                    <td
                                        key={index}
                                        className={`w-[300px] align-top px-2 border-l border-gray-300 border-b-transparent ${index < rfqData?.suppliers?.length - 1 ? 'border' : ''}`}
                                    >
                                        <div className='relative bg-[#E7EAEF] flex flex-wrap items-center justify-end gap-2'>
                                            {renderSummaryRevisionTrail(supplier, "material-amount")}
                                            <p className='text-xs bg-[#E7EAEF] w-fit text-right text-[#004491] px-2 py-1'>
                                                Rs. {
                                                    new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(
                                                        showSelectedValueColumn && rfqData?.rfqType === 'material'
                                                            ? selectedMaterialAmount
                                                            : showSelectedValueColumn && rfqData?.rfqType === 'labour'
                                                                ? selectedLabourAmount
                                                                : showSelectedValueColumn && rfqData?.rfqType === 'contractor'
                                                                    ? selectedContractorAmount
                                                            : selectedRatesTab && rfqData?.rfqType !== 'contractor'
                                                                ? (vendorQuotation?.selectedTotalWithoutGst ?? vendorQuotation?.totalWithoutGst ?? 0)
                                                                : (vendorQuotation?.totalWithoutGst ?? 0)
                                                    )
                                                }
                                            </p>
                                        </div>
                                    </td>
                                );
                            })}
                        </tr>

                        {rfqData?.rfqType === "material" && (canSelectRates || (isClosedMaterialRfq && hasStoredCommercialDiscount)) && (
                            <tr>
                                <div className='h-full sticky left-0 bg-white' style={{ boxShadow: '4px 0 4px rgba(0, 0, 0, 0.1)' }}>
                                    <td
                                        className='sticky left-0 px-4 bg-white min-w-[480px]'
                                        style={{ boxShadow: '4px 0 4px rgba(0, 0, 0, 0.1)' }}
                                    >
                                        <p className='border-b w-full h-11 text-[#6B4E00] bg-[#FFF7D6] flex justify-between items-center text-xs px-2 relative z-30'>
                                            <span>Commercial Discount (%)</span>
                                            <span className="text-[11px] text-[#8A6A00]">
                                                {isClosedMaterialRfq && !canSelectRates ? "Applied after closure" : "Optional"}
                                            </span>
                                        </p>
                                    </td>
                                </div>

                                {sortedSuppliers?.map((supplier, index) => {
                                    const supplierId = supplier?.supplierId;
                                    const selectedMaterialAmount = getMaterialVendorSelectedAmount(supplierId);
                                    const isDisabled = isEditRatesLocked;
                                    const editedValue = discountOnBasicAmountInputs?.[supplierId];
                                    const latestSet = getLatestQuotationSet(supplier);
                                    const latestValue = latestSet?.discountOnBasicAmountPercent;
                                    const inputValue = editedValue !== undefined && editedValue !== null
                                        ? editedValue
                                        : latestValue ?? "";
                                    const shouldShowClosedValue = isClosedMaterialRfq && !canSelectRates;
                                    const discountAmount = shouldShowClosedValue
                                        ? parseNumber(latestSet?.discountOnBasicAmount)
                                        : getDiscountOnBasicAmount(supplierId, selectedMaterialAmount, supplier);

                                    return (
                                        <td
                                            key={`discount-basic-${supplierId || index}`}
                                            className={`w-[300px] px-2 border-l border-gray-300 ${index < rfqData?.suppliers?.length - 1 ? 'border' : ''}`}
                                        >
                                            <div className='flex h-11 items-center justify-between gap-2 bg-[#FFF7D6] px-2'>
                                                <div className="flex min-w-[118px] items-center gap-1">
                                                    {shouldShowClosedValue ? (
                                                        <span className="min-w-[72px] text-right text-xs font-semibold text-[#6B4E00]">
                                                            {inputValue || 0}%
                                                        </span>
                                                    ) : (
                                                        <>
                                                            <input
                                                                type="text"
                                                                inputMode="decimal"
                                                                value={inputValue}
                                                                disabled={isDisabled}
                                                                onChange={(event) => handleDiscountOnBasicAmountChange(supplierId, event.target.value)}
                                                                placeholder="0"
                                                                className={`h-7 w-16 rounded border px-2 text-right text-xs focus:ring-0 ${isDisabled ? "border-gray-200 bg-gray-100 text-gray-400" : "border-amber-300 bg-white text-[#6B4E00]"}`}
                                                            />
                                                            <span className="text-xs text-[#6B4E00]">%</span>
                                                        </>
                                                    )}
                                                </div>
                                                <span className="min-w-[95px] text-right text-[11px] text-[#8A6A00]">
                                                    Rs. {new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(discountAmount)}
                                                </span>
                                            </div>
                                        </td>
                                    );
                                })}
                            </tr>
                        )}

                        {/* extra charges */}
                        {hasVisibleCharges && <ComparisonExtraCharges
                            rfqData={rfqData}
                            sortedSuppliers={sortedSuppliers}
                            withGst={withGst}
                            closeRfqForm={closeRfqForm}
                            onTotalLowestPriceSumChange={handleOtherTotalLowestPriceSum}
                            isAdvancedTransport={isAdvancedTransport}
                            selectedMaterialsMap={effectiveSelectedMaterialsMap}
                            chargeInputs={chargeInputs}
                            setChargeInputs={setChargeInputs}
                            selectedRatesTab={selectedRatesTab}
                            isEditRatesLocked={isEditRatesLocked}
                            hiddenLineItemKeys={hiddenLineItemKeys}
                            getLineItemKey={getComparisonLineItemKey}
                            renderLineItemHideButton={renderLineItemHideButton}
                        />}


                        {/* extra charges total Row */}
                        {(rfqData?.rfqType === "material" || rfqData?.rfqType === "labour") && hasVisibleCharges && !isAdvancedTransport && <tr>
                            {/*extra charges total Row  */}
                            <div className='h-full sticky left-0 bg-white' style={{ boxShadow: '4px 0 4px rgba(0, 0, 0, 0.1)', }}>
                                <td className={`sticky left-0 px-4 h-full min-w-[480px]`} style={{ boxShadow: '4px 0 4px rgba(0, 0, 0, 0.1)', }}>
                                    <p className=' w-full text-[#004491] py-1 bg-[#E7EAEF] flex justify-between items-center  text-xs px-2  '>
                                        <span>
                                            Total (Extra Charges)
                                        </span>
                                        <span>
                                            {(rfqData?.rfqType === 'material' || rfqData?.rfqType === 'labour') && (
                                                <div className='flex items-center text-xs text-[#004491]'>
                                                    <p>Rs. {otherstotalLowestPriceSum.toFixed(2)}</p>
                                                </div>
                                            )}
                                        </span>
                                    </p>
                                </td>
                            </div>

                            {/* extra charges total Row */}
                            {sortedSuppliers?.map((supplier, index) => {
                                const vendorQuotation = supplier?.vendorQuotations?.quotationSets?.slice(-1)?.[0];
                                const vendorTotal = vendorQuotation?.othersTotalWithoutGst ?? 0;
                                const updatedValues = chargeInputs?.[supplier.supplierId];
                                const updatedTotal = updatedValues
                                    ? Object.values(updatedValues).reduce((sum, val) => sum + (Number(val) || 0), 0)
                                    : vendorTotal;
                                const finalTotal =
                                    updatedValues && Object.keys(updatedValues).length > 0
                                        ? updatedTotal
                                        : vendorTotal;

                                return (
                                    <td
                                        key={index}
                                        className={`px-2 border-l border-gray-300 ${index < rfqData?.suppliers?.length - 1 ? 'border' : ''}`}
                                    >
                                        <div className='flex justify-end'>
                                            <p className='text-xs bg-[#E7EAEF] w-fit text-right text-[#004491] px-2 py-1'>
                                                Rs. {new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(finalTotal)}
                                            </p>
                                        </div>
                                    </td>
                                );
                            })}

                        </tr>}

                        {/* total amount */}
                        <tr>
                            <div className={`${(rfqData?.rfqType === "material" || rfqData?.rfqType === "labour") && ""} h-full sticky min-w-[480px] left-0 z-[120] bg-white`} style={{ boxShadow: '4px 0 4px rgba(0, 0, 0, 0.1)', }}>
                                <td className={`comparison-sticky-rfq-cell sticky left-0 align-top px-4 pt-2 bg-white min-w-[480px]`} style={{ boxShadow: '4px 0 4px rgba(0, 0, 0, 0.1)', }}>
                                    <div className='flex justify-between w-full text-[11px] py-1 text-[#007366] border-t border-[#007366] px-2 bg-[#E0F0EE]'>
                                        <span>
                                            {rfqData?.rfqType === 'material'
                                                ? 'Total (Material Amt.'
                                                : rfqData?.rfqType === 'labour'
                                                    ? 'Total (Basic Amount'
                                                    : rfqData?.rfqType === 'contractor'
                                                        ? 'Total (Sub Total'
                                                        : null
                                            }
                                            {hasVisibleCharges && ') + Extra Charges'}
                                            {rfqData?.rfqType === 'material' || rfqData?.rfqType === 'labour' || rfqData?.rfqType === 'contractor' ? ')' : ''}
                                        </span>


                                        <span>
                                            Rs. {(new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format((rfqData?.rfqType === 'material' ? totalLowestPriceSum : 0) +
                                                (rfqData?.rfqType === 'labour' ? totalLowestLabourPriceSum : 0) +
                                                otherstotalLowestPriceSum
                                            ))}
                                        </span>

                                    </div>
                                </td>
                            </div>
                            {sortedSuppliers?.map((supplier, index) => {
                                const vendorQuotation = supplier.vendorQuotations;
                                const materialSelectedTotal = getMaterialVendorSelectedTotalWithoutGst(supplier);
                                const labourSelectedTotal = getLabourVendorSelectedTotalWithoutGst(supplier);

                                return (
                                    <td
                                        key={index}
                                        className={`${rfqData?.rfqType === "labour" ? "w-[340px]" : "w-[300px]"} align-top pt-2 border-l border-l-gray-300 px-2 ${index < rfqData?.suppliers?.length - 1 ? "border" : ""}`}
                                        style={{ borderBottom: "none" }}
                                    >
                                        <div className={`${(rfqData?.rfqType === "material" || rfqData?.rfqType === "labour") && ""} relative flex min-h-[28px] flex-wrap items-center justify-end gap-2 px-1 bg-[#E0F0EE] border-t border-t-[#007366]`}>
                                            {renderSummaryRevisionTrail(supplier, "material-plus-charges")}
                                            <p className="text-xs text-[#007366] w-fit text-right px-1 py-1">
                                                Rs.{" "}
                                                {
                                                    new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(
                                                        showSelectedValueColumn && rfqData?.rfqType === 'material'
                                                            ? materialSelectedTotal
                                                            : showSelectedValueColumn && rfqData?.rfqType === 'labour'
                                                                ? labourSelectedTotal
                                                            : (vendorQuotation?.grandTotalWithoutGst ?? 0)
                                                    )
                                                }
                                            </p>
                                        </div>
                                    </td>
                                );
                            })}
                        </tr>

                        {/* total GST */}
                        <tr>
                            {/* Sticky Left Column for "Extra Charges" */}
                            <div className='h-full sticky left-0 min-w-[480px] bg-white' style={{ boxShadow: '4px 0 4px rgba(0, 0, 0, 0.1)', }}>
                                <td
                                    className="sticky left-0 z-10 "
                                    style={{ boxShadow: '4px 0 4px rgba(0, 0, 0, 0.1)' }}
                                >
                                    <p className="w-full text-xs py-2 px-6 text-[#2D2D11]">Total GST</p>
                                </td>
                            </div>

                            {/* Mapping through suppliers to show their GST differences */}
                            {sortedSuppliers.map((supplier, index) => {
                                const vendorQuotation = supplier.vendorQuotations;
                                const materialSelectedTotalWithoutGst = getMaterialVendorSelectedTotalWithoutGst(supplier);
                                const materialSelectedTotalWithGst = getMaterialVendorSelectedTotalWithGst(supplier);
                                const labourSelectedTotalWithoutGst = getLabourVendorSelectedTotalWithoutGst(supplier);
                                const labourSelectedTotalWithGst = getLabourVendorSelectedTotalWithGst(supplier);
                                return (
                                    <td key={index} className={`border border-gray-300 text-xs text-end px-4 py-2 ${rfqData?.rfqType === "labour" ? "w-[340px]" : "w-[300px]"}`}>
                                        Rs. {new Intl.NumberFormat('en-IN').format(
                                            showSelectedValueColumn && rfqData?.rfqType === 'material'
                                                ? (materialSelectedTotalWithGst - materialSelectedTotalWithoutGst)
                                                : showSelectedValueColumn && rfqData?.rfqType === 'labour'
                                                    ? (labourSelectedTotalWithGst - labourSelectedTotalWithoutGst)
                                                : (vendorQuotation?.grandTotalWithGst - vendorQuotation?.grandTotalWithoutGst || 0)
                                        )}
                                    </td>
                                );
                            })}
                        </tr>

                        {/* total quotation value */}
                        <tr>
                            {/* Sticky Left Column for "Extra Charges" */}
                            <div className='h-full sticky left-0 min-w-[480px] bg-white ' style={{ boxShadow: '4px 0 4px rgba(0, 0, 0, 0.1)', }}>
                                <td
                                    className={`sticky left-0 z-10 `}
                                    style={{ boxShadow: '4px 0 4px rgba(0, 0, 0, 0.1)' }}
                                >
                                    <p className="w-full text-xs py-2 px-6 text-[#2D2D11]">Total Quotation Value (Incl. GST)</p>
                                </td>
                            </div>

                            {/* Mapping through suppliers to show their GST differences */}
                            {sortedSuppliers.map((supplier, index) => {
                                const vendorQuotation = supplier.vendorQuotations;
                                const materialSelectedTotalWithGst = getMaterialVendorSelectedTotalWithGst(supplier);
                                const labourSelectedTotalWithGst = getLabourVendorSelectedTotalWithGst(supplier);

                                return (
                                    <td
                                        key={index}
                                        className={`border border-gray-300 text-xs text-end px-4 py-2 ${rfqData?.rfqType === "labour" ? "w-[340px]" : "w-[300px]"
                                            }`}
                                    >
                                        Rs.{" "}
                                        {new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(
                                            showSelectedValueColumn && rfqData?.rfqType === 'material'
                                                ? materialSelectedTotalWithGst
                                                : showSelectedValueColumn && rfqData?.rfqType === 'labour'
                                                    ? labourSelectedTotalWithGst
                                                : (supplier?.vendorQuotations?.grandTotalWithGst || 0)
                                        )}
                                    </td>
                                );
                            })}
                        </tr>

                        {/* deviation from lowest quote */}
                        <tr>
                            <div className='sticky left-0 border-r bg-white min-w-[480px]' style={{ boxShadow: '4px 0 4px rgba(0, 0, 0, 0.1)', }}>
                                <td className={`sticky left-0 px-6 `}>
                                    <p className='w-full text-sm py-[5px] font-bold text-[#2D2D11]'>Difference from Lowest Quote</p>
                                </td>
                            </div>

                            {/* {sortedSuppliers?.map((supplier, index) => (
                            <td
                                key={index}
                                className={`${rfqData?.rfqType === "labour" ? "w-[340px]" : "w-[300px]"} px-5 text-xs border-b border-gray-300 ${index < sortedSuppliers?.length - 1 ? 'border-b border-x' : ''}`}
                            >
                            </td>
                        ))} */}
                            {sortedSuppliers?.map((supplier, index) => {
                                const vendorQuotation = supplier.vendorQuotations;
                                const selectedComparisonTotal =
                                    showSelectedValueColumn && rfqData?.rfqType === 'material'
                                        ? (withGst
                                            ? getMaterialVendorSelectedTotalWithGst(supplier)
                                            : getMaterialVendorSelectedTotalWithoutGst(supplier))
                                        : showSelectedValueColumn && rfqData?.rfqType === 'labour'
                                            ? (withGst
                                                ? getLabourVendorSelectedTotalWithGst(supplier)
                                                : getLabourVendorSelectedTotalWithoutGst(supplier))
                                            : 0;
                                const materialComparisonTotals = sortedSuppliers
                                    .map(vendor =>
                                        withGst
                                            ? getMaterialVendorSelectedTotalWithGst(vendor)
                                            : getMaterialVendorSelectedTotalWithoutGst(vendor)
                                    )
                                    .filter(total => total > 0);
                                const materialLowestTotal = materialComparisonTotals.length
                                    ? Math.min(...materialComparisonTotals)
                                    : 0;
                                const labourComparisonTotals = sortedSuppliers
                                    .map(vendor =>
                                        withGst
                                            ? getLabourVendorSelectedTotalWithGst(vendor)
                                            : getLabourVendorSelectedTotalWithoutGst(vendor)
                                    )
                                    .filter(total => total > 0);
                                const labourLowestTotal = labourComparisonTotals.length
                                    ? Math.min(...labourComparisonTotals)
                                    : 0;

                                // Use the appropriate grand total based on withGst state
                                const grandTotal = showSelectedValueColumn && rfqData?.rfqType === 'material'
                                    ? selectedComparisonTotal
                                    : showSelectedValueColumn && rfqData?.rfqType === 'labour'
                                        ? selectedComparisonTotal
                                    : withGst
                                        ? vendorQuotation?.grandTotalWithGst || 0
                                        : vendorQuotation?.grandTotalWithoutGst || 0;

                                // Calculate L1 supplier data
                                const l1Supplier = sortedSuppliers[0]; // Assuming sortedSuppliers is sorted by total
                                const l1Total = showSelectedValueColumn && rfqData?.rfqType === 'material'
                                    ? materialLowestTotal
                                    : showSelectedValueColumn && rfqData?.rfqType === 'labour'
                                        ? labourLowestTotal
                                    : l1Supplier
                                        ? (withGst
                                            ? l1Supplier?.vendorQuotations?.grandTotalWithGst || 0
                                            : l1Supplier?.vendorQuotations?.grandTotalWithoutGst || 0)
                                        : 0;

                                // Calculate deviation
                                const deviation = l1Total ? grandTotal - l1Total : 0;
                                const deviationPercentage = l1Total ? ((deviation / l1Total) * 100).toFixed(2) : 0;

                                return (
                                    <td
                                        key={index}
                                        className={`${rfqData?.rfqType === "labour" ? "w-[340px]" : "w-[300px]"} px-4 border-t border-gray-300 ${index < rfqData?.suppliers?.length - 1 ? 'border-t border-x' : ''}`}
                                    >
                                        {showSelectedValueColumn && (rfqData?.rfqType === 'material' || rfqData?.rfqType === 'labour') ? (
                                            grandTotal > 0 ? (
                                                deviation === 0 ? (
                                                    <p className='text-xs text-right pt-2 font-medium'>Lowest Quote</p>
                                                ) : (
                                                    <p className='text-xs text-right pt-2'>
                                                        Rs. {new Intl.NumberFormat('en-IN').format(deviation)} ({deviationPercentage}%)
                                                    </p>
                                                )
                                            ) : (
                                                <p className='text-xs text-right pt-2'>-</p>
                                            )
                                        ) : index === 0 ? (
                                            <p className='text-xs text-right pt-2 font-medium'>Lowest Quote</p>
                                        ) : (
                                            <p className='text-xs text-right pt-2'>
                                                Rs. {new Intl.NumberFormat('en-IN').format(deviation)} ({deviationPercentage}%)
                                            </p>
                                        )}
                                    </td>
                                );
                            })}
                        </tr>


                        {/* Negotiation Outcome */}
                        <tr>
                            <div className='sticky left-0 bg-white min-w-[480px] border-r' style={{ boxShadow: '4px 0 4px rgba(0, 0, 0, 0.1)', }}>
                                <td className={`sticky left-0 px-6 `} style={{ boxShadow: '4px 0 4px rgba(0, 0, 0, 0.1)', }}>
                                    <p className='w-full text-sm pt-[5px] font-bold text-[#2D2D11]'>Negotiation Outcome </p>
                                    <p className='w-full text-[10px] text-[grey]'>Difference between the last & first quote</p>
                                </td>
                            </div>
                            {sortedSuppliers?.map((supplier, index) => {
                                const vendorQuotation = supplier?.vendorQuotations?.quotationSets;

                                // Use the appropriate grand total based on withGst state
                                const firstQuotation = withGst
                                    ? (vendorQuotation?.[0]?.totalWithGst + vendorQuotation?.[0]?.othersTotalWithGst) || 0
                                    : (vendorQuotation?.[0]?.totalWithoutGst + vendorQuotation?.[0]?.othersTotalWithoutGst) || 0;

                                const lastQuotation = withGst
                                    ? (vendorQuotation?.slice(-1)[0]?.totalWithGst + vendorQuotation?.slice(-1)[0]?.othersTotalWithGst) || 0
                                    : (vendorQuotation?.slice(-1)[0]?.totalWithoutGst + vendorQuotation?.slice(-1)[0]?.othersTotalWithoutGst) || 0;

                                // Calculate difference and percentage
                                const difference = firstQuotation - lastQuotation;
                                const negotiationPercentage =
                                    firstQuotation > 0 ? ((difference / firstQuotation) * 100).toFixed(2) : 0;

                                return (
                                    <td
                                        key={index}
                                        className={`${rfqData?.rfqType === "labour" ? "w-[340px]" : "w-[300px]"} px-4 border-t border-gray-300 ${index < sortedSuppliers?.length - 1 ? "border-t border-x" : ""
                                            }`}
                                    >
                                        {firstQuotation === lastQuotation ? (
                                            <p className="text-xs text-right pt-2 font-medium">Not Updated</p>
                                        ) : (
                                            <p className="text-xs text-right pt-2">
                                                Rs. {new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(difference)} ({negotiationPercentage}%)
                                            </p>
                                        )}
                                    </td>
                                );
                            })}
                        </tr>


                        {/* vendor source */}
                        {/* {hasVendorSource && (
                            <tr>
                            <div
                                className="sticky left-0 border-r h-full bg-white"
                                style={{ boxShadow: '4px 0 4px rgba(0, 0, 0, 0.1)' }}
                            >
                                <td className="sticky pt-4 left-0 px-6">
                                <p className="w-full text-sm font-bold text-[#2D2D11]">
                                    Vendor Source
                                </p>
                                </td>
                            </div>

                            {sortedSuppliers?.map((supplier, index) => (
                                <td
                                key={supplier.supplierId}
                                className={`${rfqData?.rfqType === "labour" ? "w-[340px]" : "w-[300px]"}
                                    px-4 py-2 bg-white border-t border-b border-gray-300
                                    ${index < rfqData?.suppliers?.length - 1 ? "border" : ""}`}
                                >
                                <p className="text-xs ">
                                    {supplier?.vendorSource
                                    ? supplier.vendorSource === "MARKETPLACE"
                                        ? "Marketplace"
                                        : supplier.vendorSource === "INTERNAL"
                                        ? "Internal"
                                        : supplier.vendorSource
                                    : "N/A"}
                                </p>
                                </td>
                            ))}
                            </tr>)} */}

                        {/* attachment */}
                        {rfqData?.rfqType === "material" && (
                            <tr>
                                <div className='sticky left-0 border-r h-full bg-white' style={{ boxShadow: '4px 0 4px rgba(0, 0, 0, 0.1)', }}>
                                    <td className={`sticky pt-4 left-0 px-6`}>
                                        <p className='w-full text-sm font-bold text-[#2D2D11]'>Delivery Date</p>
                                    </td>
                                </div>
                                {sortedSuppliers?.map((supplier, index) => {
                                    const vendorQuotation = supplier?.vendorQuotations?.quotationSets?.slice(-1)?.[0];
                                    return (
                                        <td
                                            key={`delivery-date-${supplier?.supplierId || index}`}
                                            className={`${rfqData?.rfqType === "labour" ? "w-[340px]" : "w-[300px]"} px-4 py-2 bg-white border-t border-b border-gray-300 ${index < rfqData?.suppliers?.length - 1 ? 'border' : ''}`}
                                        >
                                            <p className='text-xs'>{formatIstDate(vendorQuotation?.deliveryDate)}</p>
                                        </td>
                                    );
                                })}
                            </tr>
                        )}

                        <tr>
                            <div className='sticky left-0 border-r h-full bg-white' style={{ boxShadow: '4px 0 4px rgba(0, 0, 0, 0.1)', }}>
                                <td className={`sticky pt-4 left-0 px-6  `}>
                                    <p className='w-full text-sm font-bold text-[#2D2D11]'>Vendor Attachments</p>
                                </td>
                            </div>
                            {sortedSuppliers?.map((supplier, index) => {
                                const vendorQuotation = supplier?.vendorQuotations?.quotationSets?.slice(-1)?.[0];
                                return (
                                    <td className={`${rfqData?.rfqType === "labour" ? "w-[340px]" : "w-[300px]"} px-4 py-2 bg-white border-t border-b border-gray-300 ${index < rfqData?.suppliers?.length - 1 ? 'border' : ''}`}>
                                        {vendorQuotation?.attachments?.length > 0 ? (
                                            <div >
                                                {/* <h3 className="text-xs font-medium text-[#434343]">Attachments</h3> */}
                                                <div className="space-y-2">
                                                    {vendorQuotation?.attachments?.map((attachment, index) => {
                                                        const lastIndex = attachment.Name.lastIndexOf('.'); // Extract file extension
                                                        const path = lastIndex !== -1 ? attachment.Name.substring(lastIndex + 1) : null;
                                                        return (
                                                            <div key={index} className="flex justify-between items-center mt-2">
                                                                <div className="px-1 flex items-center justify-center border border-gray-300 rounded-full py-1 space-x-1 text-[#434343]">
                                                                    {/* Dynamic icon based on file type */}
                                                                    <img src={`/assets/icons/${path}.png`} alt={path} className="h-5 w-5" />
                                                                    {/* Document name now clickable */}
                                                                    <a
                                                                        href={attachment.Content}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        download={attachment.Name}
                                                                        className="text-xs truncate text-[#434343] hover:underline"
                                                                    >
                                                                        {attachment.Name}
                                                                    </a>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        ) : <p className='text-xs'>N/A</p>}
                                    </td>
                                );
                            })}
                            {/* {sortedSuppliers?.map((supplier, index) => (
                            <td
                                key={index}
                                className={`${rfqData?.rfqType === "labour" ? "w-[340px]" : "w-[300px]"} px-5 border-b text-xs border-gray-300 ${index < sortedSuppliers?.length - 1 ? 'border-b border-x' : ''}`}
                            >
                            </td>
                        ))} */}
                        </tr>

                        {/* <tr>
                        <div className='h-full sticky left-0 bg-white w-full' style={{ boxShadow: '4px 0 4px rgba(0, 0, 0, 0.1)', }}>
                            <td className="sticky left-0 h-full text-xs border-r">
                            </td>
                        </div>
                        </tr> */}

                        {/* payment terms */}
                        {/* <tr>
                        <div className='h-full sticky left-0 bg-white border-r min-w-[480px]' style={{ boxShadow: '4px 0 4px rgba(0, 0, 0, 0.1)', }}>
                            <td className={`sticky left-0 px-6`} style={{ boxShadow: '4px 0 4px rgba(0, 0, 0, 0.1)', }}>
                                <p className='w-full text-sm py-2 font-bold text-[#2D2D11]'>Payment Terms</p>
                            </td>
                        </div>
                        {sortedSuppliers?.map((supplier, index) => (
                            <td
                                key={index}
                                className={`${rfqData?.rfqType === "labour" ? "w-[340px]" : "w-[300px]"} px-4 border-b text-xs border-gray-300 ${index < sortedSuppliers?.length - 1 ? 'border-b border-x' : ''}`}
                            >
                            </td>
                        ))}
                    </tr> */}
                        <tr>
                            <div className='h-full sticky left-0 bg-white border-r min-w-[480px]' style={{ boxShadow: '4px 0 4px rgba(0, 0, 0, 0.1)', }}>
                                <td className={`sticky left-0 px-6 py-2 text-xs `}>
                                    <p className='w-full text-sm pt-2 font-bold text-[#2D2D11]'>Payment Terms</p><br />
                                    {/* {rfqData?.paymentTerms} */}{ReactHtmlParser(rfqData?.paymentTerms || 'N/A')}
                                </td>
                            </div>
                            {sortedSuppliers?.map((supplier, index) => {
                                const vendorQuotation = supplier?.vendorQuotations?.quotationSets?.slice(-1)?.[0];
                                return (
                                    <td className={`${rfqData?.rfqType === "labour" ? "w-[340px]" : "w-[300px]"} px-4 py-2 border-t border-b-0 border-gray-300 ${index < rfqData?.suppliers?.length - 1 ? 'border' : ''}`}>
                                        {/* <p className='text-sm py-2 '> <b>Counter Offer :</b>   {vendorQuotation?.agreedToPaymentTerms ? "Agreed to payment terms" : vendorQuotation?.counterPaymentTerms}</p> */}
                                        {vendorQuotation?.agreedToPaymentTerms ? <p className='text-xs'>{ReactHtmlParser(rfqData?.paymentTerms || 'N/A')}</p>
                                            : <p className='text-xs self-start'> {vendorQuotation?.counterPaymentTerms?.length > 0 && <b>Counter Offer :</b>}   {vendorQuotation?.counterPaymentTerms || "Agreed to payment terms"}</p>}
                                    </td>
                                );
                            })}
                        </tr>

                        {/* vendor notes */}
                        <tr>
                            <div className='h-full sticky left-0 bg-white border-r' style={{ boxShadow: '4px 0 4px rgba(0, 0, 0, 0.1)', }}>
                                <td className={`sticky left-0 px-6 pt-2`} style={{ boxShadow: '4px 0 4px rgba(0, 0, 0, 0.1)', }}>
                                    <p className='w-full text-sm py-[5px] font-bold text-[#2D2D11]'>Vendor Notes</p>
                                </td>
                            </div>
                            {sortedSuppliers?.map((supplier, index) => {
                                const vendorQuotation = supplier?.vendorQuotations?.quotationSets?.slice(-1)?.[0];
                                return (
                                    <td className={`${rfqData?.rfqType === "labour" ? "w-[340px]" : "w-[300px]"} px-4 py-2 border-t border-gray-300 ${index < rfqData?.suppliers?.length - 1 ? 'border' : ''}`}>
                                        {/* <p className='text-sm py-2 font-bold'>Counter Offer: <span className='font-normal'> {vendor.paymentTerm}</span></p> */}
                                        <p className='text-xs py-2 self-start'> {vendorQuotation?.notes || 'N/A'}</p>
                                    </td>
                                );
                            })}
                        </tr>
                    </tbody>
                </table>

                {renderVisibilityMenuPortal()}

                <LoadingModal
                    isOpen={excelLoading || excelError !== null}
                    onClose={() => setExcelError(null)} // Close the modal on click
                >
                    {excelLoading ? (
                        <div className="flex flex-col items-center justify-center">
                            <div className="animate-spin rounded-full border-4 border-t-4 border-orange-500 border-t-gray-200 h-8 w-8 mb-4"></div>
                            <p className="text-xl font-medium text-gray-600 italic">{loadingMessage}</p>
                        </div>
                    ) : (
                        <div>
                            <p className="text-red-500 font-medium">{excelError || "Operation completed successfully!"}</p>
                        </div>
                    )}
                </LoadingModal>
            </div >
        </>
    );
}

export default ComparisontableTesting;






